<template>
  <section class="admin-swap-shell" aria-label="Administración de FOMO Swap">
    <header class="swap-heading">
      <div>
        <span class="swap-eyebrow">Administración / Reventa de entradas</span>
        <h1>
          FOMO Swap<span class="swap-market">{{ marketLabel }}</span>
        </h1>
        <p>Todo lo que necesitas para operar la reventa, en un solo lugar.</p>
      </div>
      <div class="swap-heading-actions">
        <FomoButton
          variant="quiet"
          icon="fas fa-sync-alt"
          :disabled="loading"
          @click="loadData"
          >{{ loading ? "Actualizando…" : "Actualizar" }}</FomoButton
        >
        <FomoButton variant="action" icon="fas fa-plus" @click="focusCreate"
          >Crear evento</FomoButton
        >
      </div>
    </header>

    <nav class="swap-nav" aria-label="Secciones de Swap">
      <button
        v-for="item in swapSections"
        :key="item.key"
        type="button"
        :class="{ 'is-active': activeSection === item.key }"
        :aria-current="activeSection === item.key ? 'page' : undefined"
        @click="selectSection(item.key)"
      >
        <i :class="item.icon" aria-hidden="true"></i
        ><span>{{ item.label }}</span
        ><b v-if="item.count && !loading">{{ item.count }}</b>
      </button>
    </nav>

    <div
      v-if="feedback"
      class="swap-feedback"
      :class="{ 'is-error': feedbackType === 'error' }"
      :role="feedbackType === 'error' ? 'alert' : 'status'"
    >
      <i
        :class="
          feedbackType === 'error'
            ? 'fas fa-exclamation-circle'
            : 'fas fa-check-circle'
        "
        aria-hidden="true"
      ></i
      ><span>{{ feedback }}</span>
      <FomoButton
        variant="text"
        icon="fas fa-times"
        aria-label="Cerrar aviso"
        @click="feedback = ''"
      />
    </div>

    <div
      ref="sectionContent"
      class="swap-section"
      tabindex="-1"
      :aria-busy="loading"
    >
      <div v-if="loadFailed" class="fomo-dashboard-card swap-empty">
        <i class="fas fa-exclamation-circle" aria-hidden="true"></i
        ><strong>No pudimos cargar la operación</strong>
        <p>Vuelve a intentarlo para consultar los pendientes actualizados.</p>
        <FomoButton variant="quiet" @click="loadData">Reintentar</FomoButton>
      </div>
      <template v-else>
        <template v-if="activeSection === 'overview'">
          <div class="swap-section-heading">
            <div>
              <h2>La operación, de un vistazo</h2>
              <p>
                Empieza por los pendientes y sigue cada compra hasta su entrega.
              </p>
            </div>
            <span class="swap-caption">{{
              loading
                ? "Cargando datos…"
                : `${swapEvents.length} eventos en Swap`
            }}</span>
          </div>
          <div class="swap-metrics">
            <button
              v-for="metric in attentionMetrics"
              :key="metric.key"
              type="button"
              class="fomo-dashboard-card swap-metric"
              @click="openQueue(metric.key)"
            >
              <span class="swap-metric-top"
                ><span>{{ metric.label }}</span
                ><i :class="metric.icon" aria-hidden="true"></i
              ></span>
              <strong>{{ loading ? "—" : metric.count }}</strong
              ><span class="swap-metric-bottom"
                >{{ metric.copy
                }}<i class="fas fa-arrow-right" aria-hidden="true"></i
              ></span>
            </button>
          </div>
          <div class="swap-overview-grid">
            <article class="fomo-dashboard-card swap-panel">
              <div class="swap-section-heading">
                <div>
                  <span class="swap-eyebrow">Tu siguiente paso</span>
                  <h3>Necesitan atención</h3>
                </div>
                <i class="fas fa-tasks swap-panel-icon" aria-hidden="true"></i>
              </div>
              <div v-if="loading" class="swap-empty">Cargando pendientes…</div>
              <div v-else-if="!attentionTasks.length" class="swap-empty">
                <i class="far fa-check-circle" aria-hidden="true"></i
                ><strong>Todo al día</strong>
                <p>
                  No hay revisiones ni entregas vencidas en los datos cargados.
                </p>
                <FomoButton variant="quiet" @click="selectSection('orders')"
                  >Ver compras</FomoButton
                >
              </div>
              <div v-else class="swap-task-list">
                <button
                  v-for="task in attentionTasks"
                  :key="task.key"
                  class="swap-task"
                  type="button"
                  @click="openQueue(task.key)"
                >
                  <span class="swap-task-icon"
                    ><i :class="task.icon" aria-hidden="true"></i></span
                  ><span
                    ><strong>{{ task.title }}</strong
                    ><small>{{ task.copy }}</small></span
                  ><b>{{ task.count }}</b
                  ><i class="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
              </div>
            </article>
            <article class="fomo-dashboard-card swap-panel">
              <div class="swap-section-heading">
                <div>
                  <span class="swap-eyebrow">Tu catálogo</span>
                  <h3>Próximos eventos</h3>
                </div>
                <FomoButton variant="text" @click="selectSection('events')"
                  >Ver todos</FomoButton
                >
              </div>
              <div v-if="loading" class="swap-empty">Cargando eventos…</div>
              <div v-else-if="!upcomingEvents.length" class="swap-empty">
                <i class="far fa-calendar" aria-hidden="true"></i
                ><strong>Sin próximos eventos</strong>
                <p>Crea un evento para recibir publicaciones de entradas.</p>
              </div>
              <button
                v-for="event in upcomingEvents"
                :key="event.id"
                class="swap-event-preview"
                type="button"
                @click="showEvent(event)"
              >
                <span class="swap-date"
                  ><b>{{ eventDay(event) }}</b
                  ><small>{{ eventMonth(event) }}</small></span
                ><span
                  ><strong>{{ event.name }}</strong
                  ><small
                    >{{ event.city_name || "Ciudad pendiente" }} ·
                    {{ officialTicketOptions(event).length }} categorías</small
                  ></span
                ><i class="fas fa-arrow-right" aria-hidden="true"></i>
              </button>
            </article>
          </div>
          <div class="swap-policy">
            <i class="fas fa-info-circle" aria-hidden="true"></i
            ><span
              >Condiciones predeterminadas: comisión {{ feeLabel }} · liberación
              {{ releaseLabel }}. Puedes ajustarlas al crear un evento.</span
            ><FomoButton variant="text" @click="selectSection('payouts')"
              >Ver cobros</FomoButton
            >
          </div>
        </template>

        <template v-if="activeSection === 'events'">
          <article
            v-if="showCreate"
            ref="createPanel"
            class="fomo-dashboard-card swap-panel admin-swap-create"
          >
            <div class="swap-section-heading">
              <div>
                <span class="swap-eyebrow">Catálogo / Nuevo evento</span>
                <h2>Crear un evento Swap</h2>
                <p>
                  Configura los datos del evento y las condiciones de reventa.
                </p>
              </div>
              <FomoButton
                variant="text"
                icon="fas fa-arrow-left"
                @click="closeCreate"
                >Volver a eventos</FomoButton
              >
            </div>
            <form @submit.prevent="createSwapEvent">
              <div class="workspace-form workspace-form--two admin-swap-form">
                <label class="workspace-field workspace-field--wide">
                  <span>Nombre</span>
                  <input
                    v-model.trim="form.name"
                    class="workspace-input"
                    type="text"
                    placeholder="Nombre del concierto, festival o evento"
                    required
                    ref="createName"
                  />
                </label>
                <label class="workspace-field workspace-field--wide">
                  <span>Descripción</span>
                  <textarea
                    v-model.trim="form.description"
                    class="workspace-input admin-swap-textarea"
                    rows="4"
                    placeholder="Cuéntales a los compradores de qué trata el evento."
                  />
                </label>
                <label class="workspace-field">
                  <span>Categoría</span>
                  <select
                    v-model="form.category"
                    class="workspace-input workspace-select"
                  >
                    <option value="">Selecciona categoría</option>
                    <option
                      v-for="category in categories"
                      :key="category.id"
                      :value="category.id"
                    >
                      {{ category.name }}
                    </option>
                  </select>
                </label>
                <label class="workspace-field">
                  <span>País</span>
                  <select
                    v-model="form.country"
                    class="workspace-input workspace-select"
                    @change="loadCities"
                  >
                    <option value="">Ecuador</option>
                    <option
                      v-for="country in ecuadorCountries"
                      :key="country.id"
                      :value="country.id"
                    >
                      {{ country.name }}
                    </option>
                  </select>
                </label>
                <label class="workspace-field">
                  <span>Ciudad</span>
                  <select
                    v-model="form.city"
                    class="workspace-input workspace-select"
                    @change="loadVenues"
                  >
                    <option value="">Selecciona ciudad</option>
                    <option
                      v-for="city in cities"
                      :key="city.id"
                      :value="city.id"
                    >
                      {{ city.name }}
                    </option>
                  </select>
                </label>
                <label class="workspace-field">
                  <span>Recinto</span>
                  <select
                    v-model="form.venue"
                    class="workspace-input workspace-select"
                  >
                    <option value="">Recinto pendiente</option>
                    <option
                      v-for="venue in venues"
                      :key="venue.id"
                      :value="venue.id"
                    >
                      {{ venue.name }}
                    </option>
                  </select>
                </label>
                <label class="workspace-field">
                  <span>Inicio</span>
                  <input
                    v-model="form.start_date"
                    class="workspace-input"
                    type="datetime-local"
                    required
                  />
                </label>
                <label class="workspace-field">
                  <span>Fin</span>
                  <input
                    v-model="form.end_date"
                    class="workspace-input"
                    type="datetime-local"
                    required
                  />
                </label>
                <label class="workspace-field">
                  <span>Comisión (%)</span>
                  <input
                    v-model="feePercentInput"
                    class="workspace-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </label>
                <label class="workspace-field">
                  <span>Liberar saldo después del evento (horas)</span>
                  <input
                    v-model="form.release_after_event_hours"
                    class="workspace-input"
                    type="number"
                    min="1"
                    step="1"
                  />
                </label>
              </div>

              <p
                v-if="
                  form.start_date &&
                  form.end_date &&
                  new Date(form.end_date) < new Date(form.start_date)
                "
                class="swap-field-help"
                role="alert"
              >
                La fecha de fin debe ser posterior al inicio.
              </p>
              <p class="swap-field-help">
                Comisión a cargo del comprador. Los vendedores y sus entradas
                pasan por revisión antes de aprobarse.
              </p>
              <div class="swap-form-actions">
                <FomoButton variant="text" :disabled="saving" @click="resetForm"
                  >Limpiar campos</FomoButton
                ><FomoButton
                  variant="action"
                  type="submit"
                  :disabled="saving || !canCreate"
                  >{{ saving ? "Creando…" : "Crear evento" }}</FomoButton
                >
              </div>
            </form>
          </article>
          <template v-else>
            <div class="swap-section-heading">
              <div>
                <h2>Eventos</h2>
                <p>
                  Administra el catálogo y los precios oficiales de referencia.
                </p>
              </div>
              <span class="swap-caption"
                >{{ filteredEvents.length }} de
                {{ swapEvents.length }} eventos</span
              >
            </div>
            <label class="swap-search"
              ><i class="fas fa-search" aria-hidden="true"></i
              ><input
                v-model.trim="eventSearch"
                type="search"
                aria-label="Buscar evento"
                placeholder="Buscar por evento, ciudad o recinto"
            /></label>
            <div v-if="loading" class="swap-empty">Cargando eventos…</div>
            <div
              v-else-if="!filteredEvents.length"
              class="fomo-dashboard-card swap-empty"
            >
              <i class="far fa-calendar" aria-hidden="true"></i
              ><strong>{{
                swapEvents.length
                  ? "No encontramos ese evento"
                  : "Tu catálogo empieza aquí"
              }}</strong>
              <p>
                {{
                  swapEvents.length
                    ? "Prueba otro nombre o ciudad."
                    : "Crea tu primer evento para comenzar a recibir entradas."
                }}
              </p>
              <FomoButton
                v-if="swapEvents.length"
                variant="quiet"
                @click="eventSearch = ''"
                >Limpiar búsqueda</FomoButton
              ><FomoButton v-else variant="quiet" @click="focusCreate"
                >Crear primer evento</FomoButton
              >
            </div>
            <div v-else class="swap-record-list">
              <details
                v-for="event in filteredEvents"
                :key="event.id"
                class="fomo-dashboard-card swap-record admin-swap-event-card"
                :open="expandedEventId === event.id"
                @toggle="onEventToggle(event, $event)"
              >
                <summary class="swap-record-summary">
                  <span class="swap-date"
                    ><b>{{ eventDay(event) }}</b
                    ><small>{{ eventMonth(event) }}</small></span
                  ><span class="swap-record-title"
                    ><strong>{{ event.name }}</strong
                    ><small>{{
                      [event.city_name, event.venue_name]
                        .filter(Boolean)
                        .join(" · ") || "Lugar pendiente"
                    }}</small></span
                  ><span class="swap-record-meta"
                    ><span
                      >{{
                        officialTicketOptions(event).length
                      }}
                      categorías</span
                    ><small
                      >{{ event.presentment_currency || "USD" }} · Comisión
                      {{
                        percent(
                          event.swap_config &&
                            event.swap_config.default_fee_percent
                        )
                      }}</small
                    ></span
                  ><span class="swap-open-label"
                    >Gestionar
                    <i class="fas fa-chevron-down" aria-hidden="true"></i
                  ></span>
                </summary>
                <div class="swap-record-body">
                  <p>{{ event.description || "Sin descripción." }}</p>
                  <div class="swap-inline-meta">
                    <span>{{ formatDate(event.start_date) }}</span
                    ><span
                      >Comisión a cargo del
                      {{
                        event.swap_config &&
                        event.swap_config.default_fee_bearer === "buyer"
                          ? "comprador"
                          : "vendedor"
                      }}</span
                    >
                  </div>
                  <div class="admin-swap-official-categories">
                    <div class="admin-swap-official-head">
                      <div>
                        <small>Categorías oficiales</small>
                        <strong
                          >{{
                            officialTicketOptions(event).length
                          }}
                          configuradas</strong
                        >
                      </div>
                      <FomoButton
                        variant="quiet"
                        icon=""
                        type="button"
                        :disabled="
                          loadingOfficialTicketKey === `tickets-${event.id}`
                        "
                        @click="loadOfficialTicketsForEvent(event)"
                        >Actualizar</FomoButton
                      >
                    </div>
                    <div
                      v-if="loadingOfficialTicketKey === `tickets-${event.id}`"
                      class="workspace-empty workspace-empty--compact"
                    >
                      Cargando categorías...
                    </div>
                    <div
                      v-else-if="!officialTicketOptions(event).length"
                      class="workspace-empty workspace-empty--compact"
                    >
                      Agrega una categoría para que el vendedor tenga un precio
                      de referencia.
                    </div>
                    <div v-else class="admin-swap-official-list">
                      <article
                        v-for="option in officialTicketOptions(event)"
                        :key="option.id"
                        class="admin-swap-official-pill"
                      >
                        <span>{{
                          option.is_package ? "Paquete" : "Entrada"
                        }}</span>
                        <strong>{{ option.name }}</strong>
                        <em
                          >Oficial
                          {{ money(option.official_price, option.currency) }} ·
                          Sugerido
                          {{
                            money(option.recommended_price, option.currency)
                          }}</em
                        >
                      </article>
                    </div>
                    <details class="swap-subdetails">
                      <summary>Agregar categoría oficial</summary>
                      <div class="admin-swap-official-form">
                        <label class="workspace-field">
                          <span>Nombre categoría</span>
                          <input
                            class="workspace-input"
                            type="text"
                            :value="officialTicketForm(event).name"
                            placeholder="VIP Central, Mesa 10..."
                            @input="
                              setOfficialTicketField(
                                event,
                                'name',
                                $event.target.value
                              )
                            "
                          />
                        </label>
                        <label class="workspace-field">
                          <span>Precio oficial USD</span>
                          <input
                            class="workspace-input"
                            type="number"
                            min="0.01"
                            step="0.01"
                            :value="officialTicketForm(event).price"
                            placeholder="120.00"
                            @input="
                              setOfficialTicketField(
                                event,
                                'price',
                                $event.target.value
                              )
                            "
                          />
                        </label>
                        <label class="workspace-field">
                          <span>Cantidad paquete</span>
                          <input
                            class="workspace-input"
                            type="number"
                            min="1"
                            step="1"
                            :value="officialTicketForm(event).quantity"
                            @input="
                              setOfficialTicketField(
                                event,
                                'quantity',
                                $event.target.value
                              )
                            "
                          />
                        </label>
                        <label
                          class="workspace-field admin-swap-official-description"
                        >
                          <span>Descripción opcional</span>
                          <input
                            class="workspace-input"
                            type="text"
                            :value="officialTicketForm(event).description"
                            placeholder="Información sobre esta categoría"
                            @input="
                              setOfficialTicketField(
                                event,
                                'description',
                                $event.target.value
                              )
                            "
                          />
                        </label>
                        <FomoButton
                          variant="action"
                          icon=""
                          type="button"
                          :disabled="
                            savingOfficialTicketKey === `tickets-${event.id}` ||
                            !canCreateOfficialTicket(event)
                          "
                          @click="createOfficialTicket(event)"
                          >{{
                            savingOfficialTicketKey === `tickets-${event.id}`
                              ? "Guardando..."
                              : "Agregar categoría"
                          }}</FomoButton
                        >
                      </div>
                    </details>
                  </div>

                  <div class="workspace-actions">
                    <FomoButton
                      variant="quiet"
                      icon="fas fa-pen"
                      @click="openEvent(event)"
                      >Editar evento</FomoButton
                    ><FomoButton
                      variant="text"
                      icon="fas fa-external-link-alt"
                      @click="openCheckout(event)"
                      >Ver página de venta</FomoButton
                    >
                  </div>
                </div>
              </details>
            </div>
          </template>
        </template>

        <template v-if="activeSection === 'reviews'">
          <div class="swap-section-heading">
            <div>
              <h2>Revisiones</h2>
              <p>Valida a los vendedores y la evidencia de sus entradas.</p>
            </div>
          </div>
          <div
            class="swap-review-tabs"
            role="group"
            aria-label="Tipo de revisión"
          >
            <button
              type="button"
              :class="{ 'is-active': reviewKind === 'listings' }"
              :aria-pressed="reviewKind === 'listings'"
              @click="reviewKind = 'listings'"
            >
              Entradas <b>{{ pendingListingCount }}</b></button
            ><button
              type="button"
              :class="{ 'is-active': reviewKind === 'sellers' }"
              :aria-pressed="reviewKind === 'sellers'"
              @click="reviewKind = 'sellers'"
            >
              Vendedores <b>{{ pendingSellerCount }}</b>
            </button>
          </div>
          <div class="swap-toolbar">
            <label class="swap-search"
              ><i class="fas fa-search" aria-hidden="true"></i
              ><input
                v-model.trim="reviewSearch"
                type="search"
                aria-label="Buscar revisión"
                placeholder="Buscar por nombre, evento o correo" /></label
            ><label class="swap-select"
              ><span>Mostrar</span
              ><select v-model="reviewFilter">
                <option value="pending">Por revisar</option>
                <option value="all">Todos los estados</option>
              </select></label
            ><span class="swap-caption"
              >{{
                reviewKind === "sellers"
                  ? filteredSellers.length
                  : filteredListings.length
              }}
              resultados</span
            >
          </div>
          <div v-if="loading" class="swap-empty">Cargando revisiones…</div>
          <div
            v-else-if="
              !(reviewKind === 'sellers'
                ? filteredSellers.length
                : filteredListings.length)
            "
            class="fomo-dashboard-card swap-empty"
          >
            <i class="far fa-check-circle" aria-hidden="true"></i
            ><strong>Sin resultados para esta vista</strong>
            <p>Consulta todos los estados o cambia la búsqueda.</p>
            <FomoButton
              variant="quiet"
              @click="
                reviewFilter = 'all';
                reviewSearch = '';
              "
              >Ver todos</FomoButton
            >
          </div>
          <div v-else-if="reviewKind === 'sellers'" class="swap-record-list">
            <details
              v-for="seller in filteredSellers"
              :key="seller.id"
              class="fomo-dashboard-card swap-record admin-swap-review-card"
            >
              <summary class="swap-record-summary">
                <span class="swap-record-icon"
                  ><i class="far fa-user" aria-hidden="true"></i></span
                ><span class="swap-record-title"
                  ><strong>{{
                    seller.public_name ||
                    seller.legal_name ||
                    seller.username ||
                    "Vendedor"
                  }}</strong
                  ><small>{{ seller.user_email || "Sin correo" }}</small></span
                ><span
                  :class="['workspace-status-pill', statusClass(seller.status)]"
                  >{{ opsLabel(seller.status) }}</span
                ><span class="swap-open-label"
                  >Revisar <i class="fas fa-chevron-down" aria-hidden="true"></i
                ></span>
              </summary>
              <div class="swap-record-body">
                <div class="admin-swap-card-meta">
                  <span
                    ><i class="fas fa-building"></i
                    >{{ seller.legal_name || "Nombre legal pendiente" }}</span
                  >
                  <span
                    ><i class="fas fa-check-circle"></i
                    >{{
                      seller.terms_accepted_at
                        ? "Términos aceptados"
                        : "Términos pendientes"
                    }}</span
                  >
                  <span v-if="seller.blocked_reason"
                    ><i class="fas fa-exclamation-triangle"></i
                    >{{ seller.blocked_reason }}</span
                  >
                </div>
                <div
                  class="workspace-actions workspace-actions--compact workspace-actions--card"
                >
                  <FomoButton
                    variant="action"
                    icon=""
                    type="button"
                    :disabled="
                      reviewingKey === `seller-${seller.id}` ||
                      seller.status === 'active'
                    "
                    @click="reviewSeller(seller, 'approve')"
                    >Aprobar</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="reviewingKey === `seller-${seller.id}`"
                    @click="reviewSeller(seller, 'review')"
                    >Revisar</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="reviewingKey === `seller-${seller.id}`"
                    @click="reviewSeller(seller, 'block')"
                    >Bloquear</FomoButton
                  >
                </div>
              </div>
            </details>
          </div>
          <div v-else class="swap-record-list">
            <details
              v-for="listing in filteredListings"
              :key="listing.id"
              class="fomo-dashboard-card swap-record admin-swap-review-card"
            >
              <summary class="swap-record-summary">
                <span class="swap-record-icon"
                  ><i class="fas fa-ticket-alt" aria-hidden="true"></i></span
                ><span class="swap-record-title"
                  ><strong>{{
                    listing.title || listing.event_name || "Entrada"
                  }}</strong
                  ><small
                    >{{ listing.event_name }} ·
                    {{
                      listing.seller_public_name ||
                      listing.seller_user_email ||
                      "Vendedor"
                    }}</small
                  ></span
                ><span class="swap-record-meta"
                  ><span
                    :class="[
                      'workspace-status-pill',
                      listingStatusClass(listing.status),
                    ]"
                    >{{ opsLabel(listing.status) }}</span
                  ><small
                    >{{ listing.quantity }} ×
                    {{ money(listing.unit_price, listing.currency) }}</small
                  ></span
                ><span class="swap-open-label"
                  >Revisar <i class="fas fa-chevron-down" aria-hidden="true"></i
                ></span>
              </summary>
              <div class="swap-record-body">
                <span class="swap-caption">{{
                  opsLabel(listing.delivery_method)
                }}</span>
                <div class="admin-swap-card-meta">
                  <span
                    ><i class="fas fa-map-marker-alt"></i
                    >{{
                      [
                        listing.zone_label,
                        listing.section_label,
                        listing.row_label,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Zona pendiente"
                    }}</span
                  >
                  <span
                    ><i class="fas fa-chair"></i
                    >{{ seatSummary(listing) }}</span
                  >
                  <span
                    ><i class="fas fa-calendar"></i
                    >{{ formatDate(listing.event_start_date) }}</span
                  >
                </div>
                <div class="admin-swap-proof-strip fomo-card">
                  <div>
                    <small>Evidencia de la entrada</small>
                    <strong>{{ proofStatusLabel(listing) }}</strong>
                    <span>{{ proofHelper(listing) }}</span>
                  </div>
                  <a
                    v-if="listing.proof_file_url"
                    :href="listing.proof_file_url"
                    target="_blank"
                    rel="noopener"
                    >Ver comprobante</a
                  >
                </div>
                <div
                  class="workspace-actions workspace-actions--compact workspace-actions--card"
                >
                  <FomoButton
                    variant="action"
                    icon=""
                    type="button"
                    :disabled="
                      reviewingKey === `listing-${listing.id}` ||
                      listing.status === 'approved'
                    "
                    @click="reviewListing(listing, 'approve')"
                    >Aprobar</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="reviewingKey === `listing-${listing.id}`"
                    @click="reviewListing(listing, 'review')"
                    >Pedir revisión</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="reviewingKey === `listing-${listing.id}`"
                    @click="reviewListing(listing, 'reject')"
                    >Rechazar</FomoButton
                  >
                </div>
              </div>
            </details>
          </div>
        </template>

        <article
          v-if="activeSection === 'orders'"
          class="admin-swap-orders-panel"
        >
          <div class="swap-section-heading">
            <div>
              <h2>Compras y entregas</h2>
              <p>
                Revisa el pago, coordina la entrega y consulta el saldo de cada
                compra.
              </p>
            </div>
          </div>
          <div class="swap-toolbar swap-ops-filters">
            <label class="swap-search"
              ><i class="fas fa-search" aria-hidden="true"></i
              ><input
                v-model.trim="orderSearch"
                type="search"
                aria-label="Buscar compra"
                placeholder="Número de compra o evento" /></label
            ><label class="swap-select"
              ><span>Mostrar</span
              ><select v-model="orderQueue">
                <option value="attention">Necesitan atención</option>
                <option value="pending">Pagos pendientes</option>
                <option value="delivery">Entregas vencidas</option>
                <option value="all">Todas las compras</option>
              </select></label
            ><span class="swap-caption"
              >{{ filteredOrders.length }} compras</span
            >
          </div>
          <div v-if="loading" class="swap-empty">Cargando compras…</div>
          <div
            v-else-if="!filteredOrders.length"
            class="fomo-dashboard-card swap-empty"
          >
            <i class="fas fa-receipt" aria-hidden="true"></i
            ><strong>{{
              orders.length
                ? "Sin compras para este filtro"
                : "Aún no hay compras"
            }}</strong>
            <p>
              {{
                orders.length
                  ? "Puedes consultar el historial completo."
                  : "Las compras aparecerán aquí cuando se vendan entradas."
              }}
            </p>
            <FomoButton
              v-if="orders.length"
              variant="quiet"
              @click="
                orderQueue = 'all';
                orderSearch = '';
              "
              >Ver todas las compras</FomoButton
            >
          </div>
          <div v-else class="swap-record-list">
            <details
              v-for="order in filteredOrders"
              :key="order.id"
              class="fomo-dashboard-card swap-record admin-swap-order-card"
            >
              <summary class="swap-record-summary">
                <span class="swap-record-icon"
                  ><i class="fas fa-receipt" aria-hidden="true"></i></span
                ><span class="swap-record-title"
                  ><strong>{{ order.order_number }}</strong
                  ><small>{{ order.event_name || "Evento Swap" }}</small></span
                ><span class="swap-record-meta"
                  ><strong>{{ orderTotalLabel(order) }}</strong
                  ><span
                    :class="[
                      'workspace-status-pill',
                      paymentStatusClass(order.payment_status),
                    ]"
                    >Pago {{ opsLabel(order.payment_status) }}</span
                  ></span
                ><span class="swap-open-label"
                  >Gestionar
                  <i class="fas fa-chevron-down" aria-hidden="true"></i
                ></span>
              </summary>
              <div class="swap-record-body">
                <div class="swap-inline-meta">
                  <span>{{ formatDate(order.created_at) }}</span
                  ><span>Compra: {{ opsLabel(order.status) }}</span
                  ><span>Cobro: {{ opsLabel(order.payout_status) }}</span
                  ><span v-if="order.is_multi_seller">Varios vendedores</span>
                </div>
                <div class="admin-swap-order-meta">
                  <span
                    ><i class="fas fa-ticket-alt"></i
                    >{{ orderItemCount(order) }} entradas</span
                  >
                  <span
                    ><i class="fas fa-users"></i
                    >{{ orderSellerCount(order) }} vendedores</span
                  >
                  <span
                    ><i class="fas fa-credit-card"></i
                    >{{ verificationRail(order) }}</span
                  >
                  <span
                    ><i class="fas fa-shield-alt"></i
                    >{{ verificationStatus(order) }}</span
                  >
                </div>

                <div class="admin-swap-finance-strip">
                  <span>Entrega: {{ deliveryStatusSummary(order) }}</span>
                  <span>Saldo: {{ allocationStatusSummary(order) }}</span>
                </div>

                <div
                  v-if="
                    Array.isArray(order.deliveries) && order.deliveries.length
                  "
                  class="admin-swap-delivery-review"
                >
                  <h4 class="swap-subheading">Entregas</h4>
                  <article
                    v-for="delivery in order.deliveries"
                    :key="delivery.id"
                    class="admin-swap-delivery-row fomo-card"
                  >
                    <div>
                      <strong>Entrega #{{ delivery.id }}</strong>
                      <span
                        >{{ opsLabel(delivery.status) }} ·
                        {{ opsLabel(delivery.delivery_method) }}</span
                      >
                      <div
                        v-if="
                          Array.isArray(delivery.artifacts) &&
                          delivery.artifacts.length
                        "
                        class="admin-swap-artifact-list"
                      >
                        <article
                          v-for="artifact in delivery.artifacts"
                          :key="artifact.id"
                        >
                          <small
                            >{{
                              artifact.visible_to_buyer
                                ? "Visible al comprador"
                                : "Privado"
                            }}
                            · {{ artifact.source }} ·
                            {{ artifact.artifact_type }}</small
                          >
                          <strong>{{
                            artifact.title || "Archivo de entrega"
                          }}</strong>
                          <span v-if="artifact.external_reference">{{
                            artifact.external_reference
                          }}</span>
                          <span v-if="artifact.instructions">{{
                            artifact.instructions
                          }}</span>
                          <a
                            v-if="artifact.file_url"
                            :href="artifact.file_url"
                            target="_blank"
                            rel="noopener"
                            >{{ artifact.original_name || "Archivo" }}</a
                          >
                        </article>
                      </div>
                    </div>
                    <div class="workspace-actions workspace-actions--compact">
                      <FomoButton
                        variant="quiet"
                        icon=""
                        type="button"
                        :disabled="
                          !canUpdateDelivery(
                            order,
                            delivery,
                            'delivery_in_progress'
                          )
                        "
                        @click="
                          updateDeliveryStatus(
                            order,
                            delivery,
                            'delivery_in_progress'
                          )
                        "
                        >En progreso</FomoButton
                      >
                      <FomoButton
                        variant="action"
                        icon=""
                        type="button"
                        :disabled="
                          !canUpdateDelivery(
                            order,
                            delivery,
                            'buyer_accessible'
                          )
                        "
                        @click="
                          updateDeliveryStatus(
                            order,
                            delivery,
                            'buyer_accessible'
                          )
                        "
                        >Disponible para el comprador</FomoButton
                      >
                    </div>
                    <div
                      v-if="
                        canUpdateDelivery(order, delivery, 'buyer_accessible')
                      "
                      class="admin-swap-delivery-artifact-form"
                    >
                      <label class="workspace-field">
                        <span>Título de la entrega</span>
                        <input
                          class="workspace-input"
                          type="text"
                          :value="
                            deliveryArtifactField(delivery, 'artifact_title')
                          "
                          placeholder="Ticket validado / transferencia lista"
                          @input="
                            setDeliveryArtifactField(
                              delivery,
                              'artifact_title',
                              $event.target.value
                            )
                          "
                        />
                      </label>
                      <label class="workspace-field">
                        <span>Referencia para el comprador</span>
                        <input
                          class="workspace-input"
                          type="text"
                          :value="
                            deliveryArtifactField(
                              delivery,
                              'external_reference'
                            )
                          "
                          placeholder="Folio, código externo o referencia"
                          @input="
                            setDeliveryArtifactField(
                              delivery,
                              'external_reference',
                              $event.target.value
                            )
                          "
                        />
                      </label>
                      <label class="workspace-field">
                        <span>Link externo</span>
                        <input
                          class="workspace-input"
                          type="url"
                          :value="
                            deliveryArtifactField(delivery, 'external_url')
                          "
                          placeholder="https://..."
                          @input="
                            setDeliveryArtifactField(
                              delivery,
                              'external_url',
                              $event.target.value
                            )
                          "
                        />
                      </label>
                      <label class="workspace-field">
                        <span>Archivo para el comprador</span>
                        <input
                          class="workspace-input"
                          type="file"
                          @change="setDeliveryArtifactFile(delivery, $event)"
                        />
                      </label>
                      <label class="workspace-field admin-swap-artifact-note">
                        <span>Instrucciones de entrega</span>
                        <textarea
                          class="workspace-input"
                          rows="2"
                          :value="
                            deliveryArtifactField(delivery, 'instructions')
                          "
                          placeholder="Indica cómo debe recibir o utilizar su entrada el comprador."
                          @input="
                            setDeliveryArtifactField(
                              delivery,
                              'instructions',
                              $event.target.value
                            )
                          "
                        ></textarea>
                      </label>
                    </div>
                  </article>
                </div>

                <h4 class="swap-subheading">Verificación del pago</h4>
                <div class="admin-swap-payment-form">
                  <label class="workspace-field">
                    <span>Referencia del proveedor</span>
                    <input
                      class="workspace-input"
                      type="text"
                      :value="verificationField(order, 'provider_reference')"
                      placeholder="Stripe PI, PayPal capture, transferencia..."
                      @input="
                        setVerificationField(
                          order,
                          'provider_reference',
                          $event.target.value
                        )
                      "
                    />
                  </label>
                  <label class="workspace-field admin-swap-note-field">
                    <span>Nota interna</span>
                    <input
                      class="workspace-input"
                      type="text"
                      :value="verificationField(order, 'note')"
                      placeholder="Motivo, evidencia o comentario interno"
                      @input="
                        setVerificationField(order, 'note', $event.target.value)
                      "
                    />
                  </label>
                </div>

                <div
                  class="workspace-actions workspace-actions--compact workspace-actions--card"
                >
                  <FomoButton
                    variant="action"
                    icon=""
                    type="button"
                    :disabled="
                      verifyingKey === `order-${order.order_number}` ||
                      order.payment_status === 'verified'
                    "
                    @click="verifyOrderPayment(order, 'admin_confirmed')"
                    >Confirmar pago</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="verifyingKey === `order-${order.order_number}`"
                    @click="verifyOrderPayment(order, 'mismatch')"
                    >Marcar diferencia</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="verifyingKey === `order-${order.order_number}`"
                    @click="verifyOrderPayment(order, 'failed')"
                    >Pago fallido</FomoButton
                  >
                </div>

                <details class="admin-swap-refund-details fomo-card">
                  <summary>Registrar un reembolso manual</summary>
                  <div class="admin-swap-manual-refund-form">
                    <div class="admin-swap-refund-heading">
                      <div>
                        <small>Reembolso manual</small>
                        <strong>PayPal/Rebill/Nuvei/manual</strong>
                      </div>
                      <span>{{
                        canManualRefund(order)
                          ? "Listo para registrar"
                          : "Requiere pago confirmado y comprobante."
                      }}</span>
                    </div>
                    <label class="workspace-field">
                      <span>Monto a reembolsar (USD)</span>
                      <input
                        class="workspace-input"
                        type="number"
                        min="0.01"
                        step="0.01"
                        :value="manualRefundField(order, 'amount')"
                        @input="
                          setManualRefundField(
                            order,
                            'amount',
                            $event.target.value
                          )
                        "
                      />
                    </label>
                    <label class="workspace-field">
                      <span>Proveedor</span>
                      <select
                        class="workspace-input workspace-select"
                        :value="manualRefundField(order, 'provider')"
                        @change="
                          setManualRefundField(
                            order,
                            'provider',
                            $event.target.value
                          )
                        "
                      >
                        <option
                          v-for="provider in manualRefundProviderOptions"
                          :key="provider.value"
                          :value="provider.value"
                        >
                          {{ provider.label }}
                        </option>
                      </select>
                    </label>
                    <label class="workspace-field">
                      <span>Referencia del reembolso</span>
                      <input
                        class="workspace-input"
                        type="text"
                        :value="manualRefundField(order, 'provider_reference')"
                        placeholder="Referencia entregada por el proveedor"
                        @input="
                          setManualRefundField(
                            order,
                            'provider_reference',
                            $event.target.value
                          )
                        "
                      />
                    </label>
                    <label class="workspace-field">
                      <span>Saldos afectados (opcional)</span>
                      <input
                        class="workspace-input"
                        type="text"
                        :value="manualRefundField(order, 'allocation_ids_text')"
                        placeholder="12, 13 o vacío = prorrata completa"
                        @input="
                          setManualRefundField(
                            order,
                            'allocation_ids_text',
                            $event.target.value
                          )
                        "
                      />
                    </label>
                    <label class="workspace-field">
                      <span>Comprobante privado</span>
                      <input
                        class="workspace-input"
                        type="file"
                        @change="setManualRefundFile(order, $event)"
                      />
                    </label>
                    <label class="workspace-field admin-swap-note-field">
                      <span>Nota del reembolso</span>
                      <input
                        class="workspace-input"
                        type="text"
                        :value="manualRefundField(order, 'note')"
                        placeholder="Motivo y detalle del reembolso"
                        @input="
                          setManualRefundField(
                            order,
                            'note',
                            $event.target.value
                          )
                        "
                      />
                    </label>
                    <FomoButton
                      variant="quiet"
                      icon=""
                      type="button"
                      :disabled="!canManualRefund(order)"
                      @click="submitManualRefund(order)"
                      >{{
                        manualRefundingKey === `refund-${order.order_number}`
                          ? "Registrando..."
                          : "Registrar reembolso manual"
                      }}</FomoButton
                    >
                  </div>
                </details>
              </div>
            </details>
          </div>
        </article>

        <template v-if="activeSection === 'disputes'">
          <div class="swap-section-heading">
            <div>
              <h2>Reclamos</h2>
              <p>
                Solicita evidencia y resuelve el caso antes de liberar el saldo.
              </p>
            </div>
          </div>
          <div class="swap-toolbar">
            <label class="swap-search"
              ><i class="fas fa-search" aria-hidden="true"></i
              ><input
                v-model.trim="disputeSearch"
                type="search"
                aria-label="Buscar reclamo"
                placeholder="Número de compra o evento" /></label
            ><label class="swap-select"
              ><span>Mostrar</span
              ><select v-model="disputeFilter">
                <option value="open">Abiertos</option>
                <option value="all">Todos los reclamos</option>
              </select></label
            ><span class="swap-caption"
              >{{ filteredDisputes.length }} reclamos</span
            >
          </div>
          <div v-if="loading" class="swap-empty">Cargando reclamos…</div>
          <div
            v-else-if="!filteredDisputes.length"
            class="fomo-dashboard-card swap-empty"
          >
            <i class="fas fa-shield-alt" aria-hidden="true"></i
            ><strong>Sin reclamos en esta vista</strong>
            <p>Los casos que necesiten resolución aparecerán aquí.</p>
            <FomoButton
              v-if="disputes.length"
              variant="quiet"
              @click="
                disputeFilter = 'all';
                disputeSearch = '';
              "
              >Ver historial</FomoButton
            >
          </div>
          <div v-else class="swap-record-list">
            <details
              v-for="dispute in filteredDisputes"
              :key="dispute.id"
              class="fomo-dashboard-card swap-record admin-swap-dispute-card"
            >
              <summary class="swap-record-summary">
                <span class="swap-record-icon"
                  ><i class="fas fa-shield-alt" aria-hidden="true"></i></span
                ><span class="swap-record-title"
                  ><strong>{{ dispute.order_number }}</strong
                  ><small
                    >{{ dispute.event_name || "Evento Swap" }} ·
                    {{ opsLabel(dispute.reason || "Sin motivo") }}</small
                  ></span
                ><span
                  :class="[
                    'workspace-status-pill',
                    disputeStatusClass(dispute.status),
                  ]"
                  >{{ opsLabel(dispute.status) }}</span
                ><span class="swap-open-label"
                  >Resolver
                  <i class="fas fa-chevron-down" aria-hidden="true"></i
                ></span>
              </summary>
              <div class="swap-record-body">
                <span class="swap-caption"
                  >Abierto {{ formatDate(dispute.opened_at) }} · Vendedor #{{
                    dispute.seller_id || "—"
                  }}</span
                >
                <div class="admin-swap-card-meta">
                  <span
                    ><i class="fas fa-ticket-alt"></i>Entrada
                    {{ dispute.order_item || "n/a" }}</span
                  >
                  <span
                    ><i class="fas fa-scale-balanced"></i>Saldo
                    {{ dispute.allocation || "n/a" }}</span
                  >
                  <span v-if="dispute.resolved_at"
                    ><i class="fas fa-check"></i>Resuelto
                    {{ formatDate(dispute.resolved_at) }}</span
                  >
                </div>
                <p>{{ dispute.description || "Sin detalle del comprador." }}</p>
                <p v-if="dispute.status === 'resolved_buyer'">Reembolso aprobado, pendiente de registrar la devolución. El cobro sigue bloqueado.</p>
                <FomoButton v-if="dispute.status === 'resolved_buyer'" variant="quiet" @click="orderSearch = dispute.order_number; orderQueue = 'all'; selectSection('orders')">Ir a registrar devolución</FomoButton>

                <label class="workspace-field admin-swap-note-field">
                  <span>Nota de resolución</span>
                  <input
                    class="workspace-input"
                    type="text"
                    :value="disputeReviewField(dispute, 'resolution')"
                    placeholder="Explica la evidencia revisada y la decisión sobre este caso."
                    @input="
                      setDisputeReviewField(
                        dispute,
                        'resolution',
                        $event.target.value
                      )
                    "
                  />
                </label>

                <div
                  class="workspace-actions workspace-actions--compact workspace-actions--card"
                >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="
                      reviewingDisputeKey === `dispute-${dispute.id}` ||
                      isResolvedDispute(dispute)
                    "
                    @click="reviewDispute(dispute, 'request_evidence')"
                    >Pedir evidencia</FomoButton
                  >
                  <FomoButton
                    variant="quiet"
                    icon=""
                    type="button"
                    :disabled="
                      reviewingDisputeKey === `dispute-${dispute.id}` ||
                      isResolvedDispute(dispute)
                    "
                    @click="reviewDispute(dispute, 'resolve_buyer_refund')"
                    >Aprobar reembolso</FomoButton
                  >
                  <FomoButton
                    variant="action"
                    icon=""
                    type="button"
                    :disabled="
                      reviewingDisputeKey === `dispute-${dispute.id}` ||
                      isResolvedDispute(dispute)
                    "
                    @click="reviewDispute(dispute, 'resolve_seller')"
                    >Resolver a favor del vendedor</FomoButton
                  >
                </div>
              </div>
            </details>
          </div>
        </template>

        <template v-if="activeSection === 'messages'">
          <div class="swap-section-heading">
            <div>
              <h2>Mensajes</h2>
              <p>Consulta el contexto de cada compra y responde como FOMO.</p>
            </div>
            <span class="swap-caption"
              >{{ messageThreads.length }} conversaciones</span
            >
          </div>
          <label class="swap-search"
            ><i class="fas fa-search" aria-hidden="true"></i
            ><input
              v-model.trim="messageSearch"
              type="search"
              aria-label="Buscar conversación"
              placeholder="Buscar por compra, evento o vendedor"
          /></label>
          <div v-if="loading" class="swap-empty">Cargando conversaciones…</div>
          <div
            v-else-if="!filteredThreads.length"
            class="fomo-dashboard-card swap-empty"
          >
            <i class="far fa-comments" aria-hidden="true"></i
            ><strong>Sin conversaciones en esta vista</strong>
            <p>Los mensajes de las compras aparecerán aquí.</p>
            <FomoButton
              v-if="messageSearch"
              variant="quiet"
              @click="messageSearch = ''"
              >Limpiar búsqueda</FomoButton
            >
          </div>
          <div v-else class="swap-record-list">
            <details
              v-for="thread in filteredThreads"
              :key="thread.id"
              class="fomo-dashboard-card swap-record admin-swap-message-card"
            >
              <summary class="swap-record-summary">
                <span class="swap-record-icon"
                  ><i class="far fa-comment" aria-hidden="true"></i></span
                ><span class="swap-record-title"
                  ><strong
                    >{{ thread.order_number }} · {{ thread.event_name }}</strong
                  ><small>{{
                    thread.seller_public_name || "Conversación con FOMO"
                  }}</small></span
                ><span class="swap-caption"
                  >{{ (thread.messages || []).length }} mensajes</span
                ><span class="swap-open-label"
                  >Abrir <i class="fas fa-chevron-down" aria-hidden="true"></i
                ></span>
              </summary>
              <div class="swap-record-body">
                <div class="admin-swap-thread-messages">
                  <p
                    v-for="message in thread.messages"
                    :key="message.id"
                    :class="[
                      'admin-swap-thread-message',
                      `is-${message.sender_role}`,
                    ]"
                  >
                    <small>{{ senderLabel(message.sender_role) }}</small>
                    <span>{{ message.body }}</span>
                  </p>
                </div>
                <div class="admin-swap-message-compose">
                  <textarea
                    class="workspace-input"
                    :aria-label="`Mensaje para ${thread.order_number}`"
                    :value="messageDraft(thread.id)"
                    rows="2"
                    placeholder="Responder como FOMO"
                    @input="setMessageDraft(thread.id, $event.target.value)"
                  ></textarea>
                  <FomoButton
                    variant="action"
                    icon=""
                    type="button"
                    :disabled="
                      sendingMessageKey === `thread-${thread.id}` ||
                      !messageDraft(thread.id)
                    "
                    @click="sendAdminMessage(thread)"
                    >{{
                      sendingMessageKey === `thread-${thread.id}`
                        ? "Enviando..."
                        : "Enviar mensaje"
                    }}</FomoButton
                  >
                </div>
              </div>
            </details>
          </div>
        </template>

        <template v-if="activeSection === 'payouts'">
          <div class="swap-section-heading">
            <div>
              <h2>Cobros a vendedores</h2>
              <p>Gestiona la liberación del saldo y las órdenes de pago.</p>
            </div>
          </div>
          <div class="swap-payout-grid">
            <article class="fomo-dashboard-card swap-panel">
              <span class="swap-step">01 / Liberación</span
              ><i
                class="fas fa-lock-open swap-payout-icon"
                aria-hidden="true"
              ></i>
              <h3>Evaluar saldos</h3>
              <p>
                Libera los saldos que cumplen el plazo posterior al evento y no
                tienen reclamos pendientes.
              </p>
              <FomoButton
                variant="quiet"
                :disabled="payoutBusy || loading"
                @click="releaseEligiblePayouts"
                >{{
                  releasingPayouts ? "Evaluando…" : "Evaluar saldos"
                }}</FomoButton
              >
            </article>
            <article class="fomo-dashboard-card swap-panel">
              <span class="swap-step">02 / Preparación</span
              ><i
                class="fas fa-file-invoice-dollar swap-payout-icon"
                aria-hidden="true"
              ></i>
              <h3>Preparar pagos</h3>
              <p>
                Reserva los saldos disponibles y crea las órdenes de pago para
                los destinatarios habilitados.
              </p>
              <FomoButton
                variant="quiet"
                :disabled="payoutBusy || loading"
                @click="reservePayoutOrders"
                >{{
                  reservingPayouts ? "Preparando…" : "Preparar órdenes"
                }}</FomoButton
              >
            </article>
            <article class="fomo-dashboard-card swap-panel">
              <span class="swap-step">03 / Seguimiento</span
              ><i
                class="fas fa-sync-alt swap-payout-icon"
                aria-hidden="true"
              ></i>
              <h3>Actualizar estados</h3>
              <p>
                Sincroniza las órdenes para reflejar pagos completados y
                reservas liberadas.
              </p>
              <FomoButton
                variant="quiet"
                :disabled="payoutBusy || loading"
                @click="syncPayoutOrders"
                >{{
                  syncingPayouts ? "Actualizando…" : "Sincronizar pagos"
                }}</FomoButton
              >
            </article>
          </div>
          <div class="swap-policy">
            <i class="fas fa-info-circle" aria-hidden="true"></i
            ><span
              >Plazo predeterminado: {{ releaseLabel }}. Los reclamos mantienen
              el saldo retenido hasta su resolución.</span
            ><FomoButton variant="text" @click="selectSection('disputes')"
              >Ver reclamos</FomoButton
            >
          </div>
        </template>
      </template>
    </div>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from "../../common/api.services.js";
import "@/styles/fomo-dashboard.css";
import FomoButton from "@/components/ui/FomoButton.vue";

export default {
  name: "AdminSwapOpsView",
  components: { FomoButton },
  data() {
    return {
      loading: false,
      loadFailed: false,
      orderQueue: "attention",
      orderSearch: "",
      activeSection: "overview",
      showCreate: false,
      eventSearch: "",
      expandedEventId: null,
      reviewKind: "listings",
      reviewFilter: "pending",
      reviewSearch: "",
      disputeFilter: "open",
      disputeSearch: "",
      messageSearch: "",
      saving: false,
      releasingPayouts: false,
      reservingPayouts: false,
      syncingPayouts: false,
      manualRefundingKey: "",
      loadingOfficialTicketKey: "",
      savingOfficialTicketKey: "",
      feedback: "",
      feedbackType: "success",
      dashboard: null,
      swapEvents: [],
      sellers: [],
      listings: [],
      orders: [],
      disputes: [],
      messageThreads: [],
      categories: [],
      countries: [],
      cities: [],
      venues: [],
      reviewingKey: "",
      reviewingDisputeKey: "",
      verifyingKey: "",
      updatingDeliveryKey: "",
      sendingMessageKey: "",
      orderVerificationForms: {},
      manualRefundForms: {},
      deliveryArtifactForms: {},
      disputeReviewForms: {},
      messageDrafts: {},
      officialTicketPayloads: {},
      officialTicketForms: {},
      form: this.defaultForm(),
      manualRefundProviderOptions: [
        { value: "paypal", label: "PayPal" },
        { value: "rebill", label: "Rebill" },
        { value: "mercadopago", label: "Mercado Pago" },
        { value: "nuvei", label: "Nuvei" },
        { value: "bank_transfer", label: "Transferencia bancaria" },
        { value: "manual_external", label: "Manual externo" },
        { value: "other", label: "Otro" },
      ],
    };
  },
  computed: {
    swapSections() {
      return [
        { key: "overview", label: "Resumen", icon: "fas fa-border-all" },
        { key: "events", label: "Eventos", icon: "far fa-calendar" },
        {
          key: "reviews",
          label: "Revisiones",
          icon: "fas fa-user-check",
          count: this.pendingSellerCount + this.pendingListingCount,
        },
        { key: "orders", label: "Compras", icon: "fas fa-ticket-alt" },
        {
          key: "disputes",
          label: "Reclamos",
          icon: "fas fa-shield-alt",
          count: this.pendingDisputeCount,
        },
        { key: "messages", label: "Mensajes", icon: "far fa-comments" },
        { key: "payouts", label: "Cobros", icon: "fas fa-wallet" },
      ];
    },
    attentionMetrics() {
      return [
        {
          key: "payments",
          label: "Pagos por validar",
          count: this.pendingPaymentCount,
          icon: "far fa-credit-card",
          copy: "Revisar compras",
        },
        {
          key: "listings",
          label: "Entradas por revisar",
          count: this.pendingListingCount,
          icon: "fas fa-ticket-alt",
          copy: "Validar publicaciones",
        },
        {
          key: "sellers",
          label: "Vendedores por aprobar",
          count: this.pendingSellerCount,
          icon: "far fa-user",
          copy: "Revisar perfiles",
        },
        {
          key: "disputes",
          label: "Reclamos abiertos",
          count: this.pendingDisputeCount,
          icon: "fas fa-shield-alt",
          copy: "Resolver casos",
        },
      ];
    },
    attentionTasks() {
      return [
        {
          key: "disputes",
          title: "Resolver reclamos",
          copy: "Estos casos mantienen el saldo retenido.",
          icon: "fas fa-shield-alt",
          count: this.pendingDisputeCount,
        },
        {
          key: "delivery",
          title: "Coordinar entregas vencidas",
          copy: "Compras confirmadas que superaron el plazo.",
          icon: "fas fa-clock",
          count: this.overdueDeliveryCount,
        },
        {
          key: "payments",
          title: "Confirmar pagos",
          copy: "Verifica el cobro antes de continuar con la entrega.",
          icon: "far fa-credit-card",
          count: this.pendingPaymentCount,
        },
        {
          key: "listings",
          title: "Validar entradas",
          copy: "Revisa la evidencia de cada publicación.",
          icon: "fas fa-ticket-alt",
          count: this.pendingListingCount,
        },
        {
          key: "sellers",
          title: "Aprobar vendedores",
          copy: "Comprueba sus datos y términos aceptados.",
          icon: "far fa-user",
          count: this.pendingSellerCount,
        },
      ].filter((task) => task.count > 0);
    },
    overdueDeliveryCount() {
      return this.orders.filter((order) => this.isDeliveryOverdue(order))
        .length;
    },
    payoutBusy() {
      return (
        this.releasingPayouts || this.reservingPayouts || this.syncingPayouts
      );
    },
    upcomingEvents() {
      return this.swapEvents
        .filter((event) => new Date(event.start_date).getTime() >= Date.now())
        .slice()
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
        .slice(0, 3);
    },
    filteredEvents() {
      return this.swapEvents.filter((event) =>
        this.matchesSearch(
          this.eventSearch,
          event.name,
          event.city_name,
          event.venue_name
        )
      );
    },
    filteredSellers() {
      return this.sellers
        .filter(
          (seller) =>
            this.reviewFilter === "all" ||
            ["pending", "review"].includes(seller.status)
        )
        .filter((seller) =>
          this.matchesSearch(
            this.reviewSearch,
            seller.public_name,
            seller.legal_name,
            seller.username,
            seller.user_email
          )
        );
    },
    filteredListings() {
      return this.listings
        .filter(
          (listing) =>
            this.reviewFilter === "all" || listing.status === "pending_review"
        )
        .filter((listing) =>
          this.matchesSearch(
            this.reviewSearch,
            listing.title,
            listing.event_name,
            listing.seller_public_name,
            listing.seller_user_email
          )
        );
    },
    filteredDisputes() {
      return this.disputes
        .filter(
          (dispute) =>
            this.disputeFilter === "all" || dispute.status === "resolved_buyer" || !this.isResolvedDispute(dispute)
        )
        .filter((dispute) =>
          this.matchesSearch(
            this.disputeSearch,
            dispute.order_number,
            dispute.event_name
          )
        );
    },
    filteredThreads() {
      return this.messageThreads.filter((thread) =>
        this.matchesSearch(
          this.messageSearch,
          thread.order_number,
          thread.event_name,
          thread.seller_public_name
        )
      );
    },
    feePercentInput: {
      get() {
        return Number(
          (Number(this.form.default_fee_percent || 0) * 100).toFixed(4)
        );
      },
      set(value) {
        this.form.default_fee_percent = String(Number(value || 0) / 100);
      },
    },
    filteredOrders() {
      return this.orders
        .filter((order) =>
          this.matchesSearch(
            this.orderSearch,
            order.order_number,
            order.event_name
          )
        )
        .filter(
          (order) =>
            this.orderQueue === "all" ||
            (this.orderQueue === "attention" &&
              this.orderPriority(order) > 0) ||
            (this.orderQueue === "pending" && this.isPendingPayment(order)) ||
            (this.orderQueue === "delivery" && this.isDeliveryOverdue(order))
        )
        .sort(
          (a, b) =>
            this.orderPriority(b) - this.orderPriority(a) ||
            new Date(a.created_at) - new Date(b.created_at)
        );
    },
    ecuadorCountries() {
      return this.countries.filter(
        (country) => String(country.code || "").toUpperCase() === "EC"
      );
    },
    marketLabel() {
      const market = this.dashboard && this.dashboard.market;
      return market
        ? `${market.country_code} / ${market.currency}`
        : "EC / USD";
    },
    feeLabel() {
      const market = this.dashboard && this.dashboard.market;
      return this.percent(market && market.default_fee_percent);
    },
    releaseLabel() {
      const market = this.dashboard && this.dashboard.market;
      return `${
        (market && market.release_after_event_hours) || 24
      }h post-evento`;
    },
    pendingSellerCount() {
      return this.sellers.filter((seller) =>
        ["pending", "review"].includes(seller.status)
      ).length;
    },
    pendingListingCount() {
      return this.listings.filter(
        (listing) => listing.status === "pending_review"
      ).length;
    },
    pendingPaymentCount() {
      return this.orders.filter((order) => this.isPendingPayment(order)).length;
    },
    pendingDisputeCount() {
      return this.disputes.filter((dispute) => dispute.status === "resolved_buyer" || !this.isResolvedDispute(dispute))
        .length;
    },
    canCreate() {
      return Boolean(
        this.form.name &&
          this.form.start_date &&
          this.form.end_date &&
          new Date(this.form.end_date) >= new Date(this.form.start_date) &&
          this.feePercentInput >= 0 &&
          this.feePercentInput <= 100 &&
          Number(this.form.release_after_event_hours) >= 1
      );
    },
  },
  watch: {
    "$route.query.swapSection": {
      immediate: true,
      handler(value) {
        this.activeSection = this.swapSections.some(
          (item) => item.key === value
        )
          ? value
          : "overview";
      },
    },
  },
  created() {
    this.loadData();
    this.loadOptions();
  },
  methods: {
    matchesSearch(search, ...values) {
      const normalize = (value) =>
        String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
      return normalize(values.join(" ")).includes(normalize(search));
    },
    selectSection(section) {
      this.activeSection = section;
      this.$router.replace({
        query: { ...this.$route.query, swapSection: section },
      });
    },
    openQueue(key) {
      if (key === "sellers" || key === "listings") {
        this.reviewKind = key;
        this.reviewFilter = "pending";
        this.reviewSearch = "";
        this.selectSection("reviews");
      } else if (key === "disputes") {
        this.disputeFilter = "open";
        this.disputeSearch = "";
        this.selectSection("disputes");
      } else {
        this.orderQueue = key === "delivery" ? "delivery" : "pending";
        this.orderSearch = "";
        this.selectSection("orders");
      }
      this.$nextTick(() =>
        this.$refs.sectionContent?.focus({ preventScroll: true })
      );
    },
    showEvent(event) {
      this.showCreate = false;
      this.eventSearch = "";
      this.expandedEventId = event.id;
      this.selectSection("events");
    },
    onEventToggle(event, domEvent) {
      if (domEvent.target.open) this.expandedEventId = event.id;
      else if (this.expandedEventId === event.id) this.expandedEventId = null;
    },
    closeCreate() {
      this.showCreate = false;
      this.$nextTick(() =>
        this.$refs.sectionContent?.focus({ preventScroll: true })
      );
    },
    isPendingPayment(order) {
      return !["verified", "failed", "refunded", "chargeback"].includes(
        order.payment_status
      );
    },
    isDeliveryOverdue(order) {
      return (
        order.payment_status === "verified" &&
        (order.deliveries || []).some(
          (delivery) =>
            [
              "pending_seller_action",
              "seller_confirmed",
              "delivery_in_progress",
            ].includes(delivery.status) &&
            delivery.due_at &&
            new Date(delivery.due_at).getTime() < Date.now()
        )
      );
    },
    orderPriority(order) {
      if (
        order.fomo_review_status === "needs_review" ||
        order.payout_status === "blocked"
      )
        return 3;
      if (this.isDeliveryOverdue(order)) return 2;
      if (
        this.isPendingPayment(order) &&
        Date.now() - new Date(order.created_at).getTime() > 300000
      )
        return 1;
      return 0;
    },
    eventDay(event) {
      const date = new Date(event.start_date);
      return Number.isNaN(date.getTime())
        ? "—"
        : new Intl.DateTimeFormat("es-EC", {
            day: "2-digit",
            timeZone: "America/Guayaquil",
          }).format(date);
    },
    eventMonth(event) {
      const date = new Date(event.start_date);
      return Number.isNaN(date.getTime())
        ? "fecha"
        : new Intl.DateTimeFormat("es-EC", {
            month: "short",
            timeZone: "America/Guayaquil",
          })
            .format(date)
            .replace(".", "");
    },
    senderLabel(role) {
      return (
        {
          system: "Sistema FOMO",
          admin: "Equipo FOMO",
          buyer: "Comprador",
          seller: "Vendedor",
        }[role] || "Participante"
      );
    },
    opsLabel(value) {
      const labels = {
        active: "Activo",
        review: "En revisión",
        pending_review: "Por revisar",
        approved: "Aprobada",
        rejected: "Rechazada",
        blocked: "Bloqueado",
        open: "Abierto",
        evidence_requested: "Evidencia solicitada",
        resolved_seller: "Resuelto a favor del vendedor",
        resolved_buyer: "Reembolso aprobado · pendiente",
        delivery_issue: "Problema de entrega",
        ticket_invalid: "Entrada no válida",
        seller_confirmed: "Confirmada por el vendedor",
        reserved: "Reservado",
        admin_confirmed: "Confirmado por FOMO",
        mismatch: "Diferencia detectada",
        completed: "Completada",
        confirmed: "Confirmada",
        in_delivery: "En entrega",
        partially_refunded: "Reembolso parcial",
        unverified: "por verificar",
        verified: "confirmado",
        failed: "fallido",
        pending: "pendiente",
        pending_payment: "pago pendiente",
        pending_payment_verification: "Pago por verificar",
        pending_seller_action: "Por entregar",
        delivery_in_progress: "En preparación",
        buyer_accessible: "Disponible para el comprador",
        delivered: "Entregada",
        cancelled: "Cancelada",
        refunded: "Reembolsada",
        chargeback: "Con reclamo",
        physical_handoff: "Entrega física",
        app_transfer: "Transferencia por app",
        qr_code: "Archivo de entrada",
        held: "retenido",
        releasable: "disponible",
        paid: "enviado",
      };
      return labels[value] || String(value || "pendiente").replaceAll("_", " ");
    },
    defaultForm() {
      return {
        name: "",
        description: "",
        category: "",
        country: "",
        city: "",
        venue: "",
        start_date: "",
        end_date: "",
        timezone: "America/Guayaquil",
        default_fee_percent: "0.2000",
        release_after_event_hours: 24,
      };
    },
    defaultOfficialTicketForm() {
      return { name: "", description: "", price: "", quantity: 1 };
    },
    percent(value) {
      const numeric = Number(value || 0) * 100;
      return `${new Intl.NumberFormat("es-EC", {
        maximumFractionDigits: 2,
      }).format(numeric)}%`;
    },
    formatDate(value) {
      if (!value) return "Fecha pendiente";
      try {
        return new Intl.DateTimeFormat("es-EC", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(value));
      } catch (_error) {
        return value;
      }
    },
    money(value, currency = "USD") {
      const amount = Number(value || 0);
      try {
        return new Intl.NumberFormat("es-EC", {
          style: "currency",
          currency: currency || "USD",
        }).format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ${currency || "USD"}`;
      }
    },
    statusClass(status) {
      if (status === "active") return "is-active";
      if (status === "blocked") return "is-danger";
      return "is-warning";
    },
    listingStatusClass(status) {
      if (status === "approved") return "is-active";
      if (["rejected", "blocked", "cancelled"].includes(status))
        return "is-danger";
      return "is-warning";
    },
    orderStatusClass(status) {
      if (
        ["confirmed", "in_delivery", "delivered", "completed"].includes(status)
      )
        return "is-active";
      if (["cancelled", "partially_refunded"].includes(status))
        return "is-danger";
      return "is-warning";
    },
    paymentStatusClass(status) {
      if (status === "verified") return "is-active";
      if (["failed", "refunded", "chargeback"].includes(status))
        return "is-danger";
      return "is-warning";
    },
    disputeStatusClass(status) {
      if (status === "resolved_seller") return "is-active";
      if (["refunded", "resolved_buyer"].includes(status)) return "is-danger";
      return "is-warning";
    },
    isResolvedDispute(dispute) {
      return Boolean(
        dispute &&
          ["resolved_seller", "resolved_buyer", "refunded"].includes(
            dispute.status
          )
      );
    },
    seatSummary(listing) {
      const unitSeats = Array.isArray(listing.units)
        ? listing.units.map((unit) => unit.seat_label).filter(Boolean)
        : [];
      if (unitSeats.length) return unitSeats.join(", ");
      return (
        [listing.seat_start, listing.seat_end].filter(Boolean).join(" - ") ||
        (listing.seats_together ? "Juntos" : "No juntos")
      );
    },
    proofStatusLabel(listing) {
      const labels = {
        missing: "Sin evidencia",
        submitted: "Evidencia enviada",
        pending_review: "Pendiente FOMO",
        approved: "Aprobada",
        rejected: "Rechazada",
      };
      return (
        labels[listing && listing.proof_status] ||
        (listing && listing.proof_status) ||
        "Sin evidencia"
      );
    },
    proofHelper(listing) {
      const proof = (listing && listing.proof_metadata) || {};
      return (
        proof.note ||
        proof.external_reference ||
        "El vendedor debe subir archivo o referencia antes de aprobación final."
      );
    },
    officialTicketOptions(event) {
      const payload =
        this.officialTicketPayloads[(event && event.id) || ""] || {};
      return Array.isArray(payload.options) ? payload.options : [];
    },
    officialTicketForm(event) {
      return (
        this.officialTicketForms[(event && event.id) || ""] ||
        this.defaultOfficialTicketForm()
      );
    },
    setOfficialTicketField(event, field, value) {
      if (!event || !event.id) return;
      this.officialTicketForms = {
        ...this.officialTicketForms,
        [event.id]: {
          ...this.defaultOfficialTicketForm(),
          ...(this.officialTicketForms[event.id] || {}),
          [field]: value,
        },
      };
    },
    canCreateOfficialTicket(event) {
      const form = this.officialTicketForm(event);
      return Boolean(
        event &&
          event.id &&
          String(form.name || "").trim() &&
          Number(form.price || 0) > 0 &&
          Number(form.quantity || 0) >= 1
      );
    },
    orderTotals(order) {
      return (order && order.totals) || {};
    },
    orderTotalLabel(order) {
      const totals = this.orderTotals(order);
      return this.money(totals.customer_total || 0, totals.currency || "USD");
    },
    orderItemCount(order) {
      return Array.isArray(order.items)
        ? order.items.length
        : Number(this.orderTotals(order).item_count || 0);
    },
    orderSellerCount(order) {
      if (!Array.isArray(order.items))
        return Number(this.orderTotals(order).seller_count || 0);
      return new Set(order.items.map((item) => item.seller).filter(Boolean))
        .size;
    },
    verificationRail(order) {
      const verification = order && order.payment_verification;
      return verification && verification.rail
        ? verification.rail
        : "Método pendiente";
    },
    verificationStatus(order) {
      const verification = order && order.payment_verification;
      return verification && verification.status
        ? this.opsLabel(verification.status)
        : "sin verificación";
    },
    deliveryStatusSummary(order) {
      return this.statusSummary(order && order.deliveries);
    },
    allocationStatusSummary(order) {
      return this.statusSummary(order && order.finance_allocations);
    },
    statusSummary(items) {
      if (!Array.isArray(items) || !items.length) return "pendiente";
      const counts = items.reduce((acc, item) => {
        const status = item.status || "unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      return Object.keys(counts)
        .map((status) => `${this.opsLabel(status)}: ${counts[status]}`)
        .join(" · ");
    },
    syncOrderVerificationForms(orders) {
      const forms = {};
      const orderList = orders || [];
      orderList.forEach((order) => {
        const key = order.order_number;
        const existing = this.orderVerificationForms[key] || {};
        const verification = order.payment_verification || {};
        forms[key] = {
          provider_reference: Object.prototype.hasOwnProperty.call(
            existing,
            "provider_reference"
          )
            ? existing.provider_reference
            : verification.provider_reference || "",
          note: Object.prototype.hasOwnProperty.call(existing, "note")
            ? existing.note
            : "",
        };
      });
      this.orderVerificationForms = forms;
    },
    syncManualRefundForms(orders) {
      const forms = {};
      const orderList = orders || [];
      orderList.forEach((order) => {
        const key = order.order_number;
        const existing = this.manualRefundForms[key] || {};
        const rail = this.verificationRail(order);
        const provider = this.manualRefundProviderOptions.some(
          (item) => item.value === rail
        )
          ? rail
          : "paypal";
        forms[key] = {
          amount: Object.prototype.hasOwnProperty.call(existing, "amount")
            ? existing.amount
            : String(this.orderTotals(order).customer_total || ""),
          provider: Object.prototype.hasOwnProperty.call(existing, "provider")
            ? existing.provider
            : provider,
          provider_reference: Object.prototype.hasOwnProperty.call(
            existing,
            "provider_reference"
          )
            ? existing.provider_reference
            : "",
          allocation_ids_text: Object.prototype.hasOwnProperty.call(
            existing,
            "allocation_ids_text"
          )
            ? existing.allocation_ids_text
            : "",
          note: Object.prototype.hasOwnProperty.call(existing, "note")
            ? existing.note
            : "",
          proof_file: Object.prototype.hasOwnProperty.call(
            existing,
            "proof_file"
          )
            ? existing.proof_file
            : null,
        };
      });
      this.manualRefundForms = forms;
    },
    syncDeliveryArtifactForms(orders) {
      const forms = {};
      const orderList = orders || [];
      orderList.forEach((order) => {
        const deliveries = order.deliveries || [];
        deliveries.forEach((delivery) => {
          const existing = this.deliveryArtifactForms[delivery.id] || {};
          forms[delivery.id] = {
            artifact_title: Object.prototype.hasOwnProperty.call(
              existing,
              "artifact_title"
            )
              ? existing.artifact_title
              : "",
            external_reference: Object.prototype.hasOwnProperty.call(
              existing,
              "external_reference"
            )
              ? existing.external_reference
              : "",
            external_url: Object.prototype.hasOwnProperty.call(
              existing,
              "external_url"
            )
              ? existing.external_url
              : "",
            instructions: Object.prototype.hasOwnProperty.call(
              existing,
              "instructions"
            )
              ? existing.instructions
              : "",
            artifact_file: Object.prototype.hasOwnProperty.call(
              existing,
              "artifact_file"
            )
              ? existing.artifact_file
              : null,
          };
        });
      });
      this.deliveryArtifactForms = forms;
    },
    syncDisputeReviewForms(disputes) {
      const forms = {};
      const disputeList = disputes || [];
      disputeList.forEach((dispute) => {
        const key = dispute.id;
        const existing = this.disputeReviewForms[key] || {};
        forms[key] = {
          resolution: Object.prototype.hasOwnProperty.call(
            existing,
            "resolution"
          )
            ? existing.resolution
            : dispute.resolution || "",
        };
      });
      this.disputeReviewForms = forms;
    },
    verificationField(order, field) {
      const form =
        this.orderVerificationForms[(order && order.order_number) || ""] || {};
      return Object.prototype.hasOwnProperty.call(form, field)
        ? form[field]
        : "";
    },
    setVerificationField(order, field, value) {
      if (!order || !order.order_number) return;
      this.orderVerificationForms = {
        ...this.orderVerificationForms,
        [order.order_number]: {
          ...(this.orderVerificationForms[order.order_number] || {}),
          [field]: value,
        },
      };
    },
    manualRefundField(order, field) {
      const form =
        this.manualRefundForms[(order && order.order_number) || ""] || {};
      return Object.prototype.hasOwnProperty.call(form, field)
        ? form[field]
        : "";
    },
    setManualRefundField(order, field, value) {
      if (!order || !order.order_number) return;
      this.manualRefundForms = {
        ...this.manualRefundForms,
        [order.order_number]: {
          ...(this.manualRefundForms[order.order_number] || {}),
          [field]: value,
        },
      };
    },
    setManualRefundFile(order, event) {
      const file =
        event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;
      this.setManualRefundField(order, "proof_file", file);
    },
    manualRefundAllocationIds(order) {
      const text = String(
        this.manualRefundField(order, "allocation_ids_text") || ""
      );
      return text
        .split(/[\s,]+/)
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item > 0);
    },
    canManualRefund(order) {
      if (!order || !order.order_number || this.manualRefundingKey)
        return false;
      if (order.payment_status !== "verified") return false;
      const amount = Number(this.manualRefundField(order, "amount") || 0);
      const hasProof = Boolean(
        this.manualRefundField(order, "provider_reference") ||
          this.manualRefundField(order, "proof_file")
      );
      return amount > 0 && hasProof;
    },
    deliveryArtifactField(delivery, field) {
      const form =
        this.deliveryArtifactForms[(delivery && delivery.id) || ""] || {};
      return Object.prototype.hasOwnProperty.call(form, field)
        ? form[field]
        : "";
    },
    setDeliveryArtifactField(delivery, field, value) {
      if (!delivery || !delivery.id) return;
      this.deliveryArtifactForms = {
        ...this.deliveryArtifactForms,
        [delivery.id]: {
          ...(this.deliveryArtifactForms[delivery.id] || {}),
          [field]: value,
        },
      };
    },
    setDeliveryArtifactFile(delivery, event) {
      const file =
        event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;
      this.setDeliveryArtifactField(delivery, "artifact_file", file);
    },
    disputeReviewField(dispute, field) {
      const form = this.disputeReviewForms[(dispute && dispute.id) || ""] || {};
      return Object.prototype.hasOwnProperty.call(form, field)
        ? form[field]
        : "";
    },
    setDisputeReviewField(dispute, field, value) {
      if (!dispute || !dispute.id) return;
      this.disputeReviewForms = {
        ...this.disputeReviewForms,
        [dispute.id]: {
          ...(this.disputeReviewForms[dispute.id] || {}),
          [field]: value,
        },
      };
    },
    canUpdateDelivery(order, delivery, status) {
      if (!order || !delivery || this.updatingDeliveryKey) return false;
      if (order.payment_status !== "verified") return false;
      if (status === "delivery_in_progress")
        return ["pending_seller_action", "seller_confirmed"].includes(
          delivery.status
        );
      if (status === "buyer_accessible")
        return ["seller_confirmed", "delivery_in_progress"].includes(
          delivery.status
        );
      return false;
    },
    extractError(payload, fallback) {
      if (!payload) return fallback;
      if (payload.err) return payload.err;
      if (payload.detail)
        return Array.isArray(payload.detail)
          ? payload.detail.join(", ")
          : payload.detail;
      const firstValue =
        typeof payload === "object" ? Object.values(payload)[0] : null;
      if (Array.isArray(firstValue)) return firstValue[0];
      return firstValue || fallback;
    },
    toIso(value) {
      if (!value) return "";
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
    },
    async loadData(options = {}) {
      this.loading = true;
      this.loadFailed = false;
      if (!options.preserveFeedback) this.feedback = "";
      const [dashboard, events, sellers, listings, orders, disputes, messages] =
        await Promise.all([
          apiService("/api/admin/swap/dashboard/"),
          apiService("/api/admin/swap/events/"),
          apiService("/api/admin/swap/sellers/"),
          apiService("/api/admin/swap/listings/"),
          apiService("/api/admin/swap/orders/"),
          apiService("/api/admin/swap/disputes/"),
          apiService("/api/admin/swap/messages/"),
        ]);
      this.dashboard = dashboard && !dashboard.err ? dashboard : null;
      this.swapEvents = Array.isArray(events) ? events : [];
      await this.loadOfficialTicketsForEvents(this.swapEvents);
      this.sellers = Array.isArray(sellers) ? sellers : [];
      this.listings = Array.isArray(listings) ? listings : [];
      this.orders = Array.isArray(orders) ? orders : [];
      this.disputes = Array.isArray(disputes) ? disputes : [];
      this.messageThreads =
        messages && Array.isArray(messages.conversations)
          ? messages.conversations
          : [];
      this.syncOrderVerificationForms(this.orders);
      this.syncManualRefundForms(this.orders);
      this.syncDeliveryArtifactForms(this.orders);
      this.syncDisputeReviewForms(this.disputes);
      const errorPayload = [
        dashboard,
        events,
        sellers,
        listings,
        orders,
        disputes,
        messages,
      ].find(
        (payload) => payload && (payload.err || payload.detail || payload.error)
      );
      const invalidResponse =
        !dashboard ||
        ![events, sellers, listings, orders, disputes].every(Array.isArray) ||
        !Array.isArray(messages && messages.conversations);
      if (errorPayload || invalidResponse) {
        this.loadFailed = true;
        this.feedback = this.extractError(
          errorPayload,
          "No pudimos cargar Swap."
        );
        this.feedbackType = "error";
      }
      this.loading = false;
    },
    async loadOptions() {
      const [categories, countries] = await Promise.all([
        apiService("/api/category/"),
        apiService("/api/countries/"),
      ]);
      this.categories = Array.isArray(categories) ? categories : [];
      this.countries = Array.isArray(countries) ? countries : [];
      const ecuador = this.ecuadorCountries[0];
      if (ecuador && !this.form.country) {
        this.form.country = ecuador.id;
        this.loadCities();
      }
    },
    async loadCities() {
      this.form.city = "";
      this.form.venue = "";
      this.venues = [];
      if (!this.form.country) return;
      const data = await apiService(
        `/api/cities/?country_id=${encodeURIComponent(this.form.country)}`
      );
      this.cities = Array.isArray(data) ? data : [];
    },
    async loadVenues() {
      this.form.venue = "";
      if (!this.form.city) return;
      const data = await apiService(
        `/api/venues/?city_id=${encodeURIComponent(this.form.city)}`
      );
      this.venues = Array.isArray(data) ? data : [];
    },
    resetForm() {
      this.form = this.defaultForm();
      const ecuador = this.ecuadorCountries[0];
      if (ecuador) this.form.country = ecuador.id;
      this.loadCities();
    },
    focusCreate() {
      this.showCreate = true;
      this.selectSection("events");
      this.$nextTick(() => this.$refs.createName?.focus());
    },
    async createSwapEvent() {
      if (!this.canCreate || this.saving) return;
      this.saving = true;
      this.feedback = "";
      const payload = {
        name: this.form.name,
        description: this.form.description,
        category: this.form.category || null,
        country: this.form.country || null,
        city: this.form.city || null,
        venue: this.form.venue || null,
        start_date: this.toIso(this.form.start_date),
        end_date: this.toIso(this.form.end_date),
        timezone: this.form.timezone || "America/Guayaquil",
        default_fee_percent: this.form.default_fee_percent,
        release_after_event_hours: this.form.release_after_event_hours || 24,
      };
      const data = await apiService(
        "/api/admin/swap/events/create/",
        "POST",
        payload
      );
      this.saving = false;
      if (data && data.id && !data.err) {
        this.feedback = `Evento swap creado: ${data.name}`;
        this.feedbackType = "success";
        this.resetForm();
        this.showCreate = false;
        this.eventSearch = "";
        this.expandedEventId = data.id;
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos crear el evento swap."
      );
      this.feedbackType = "error";
    },
    async loadOfficialTicketsForEvents(events) {
      const eventList = Array.isArray(events) ? events : [];
      await Promise.all(
        eventList.map((event) =>
          this.loadOfficialTicketsForEvent(event, { silent: true })
        )
      );
    },
    async loadOfficialTicketsForEvent(event, options = {}) {
      if (!event || !event.id) return;
      if (!options.silent)
        this.loadingOfficialTicketKey = `tickets-${event.id}`;
      const data = await apiService(
        `/api/admin/swap/events/${event.id}/official-tickets/`
      );
      if (!options.silent) this.loadingOfficialTicketKey = "";
      if (data && !data.err && Array.isArray(data.options)) {
        this.officialTicketPayloads = {
          ...this.officialTicketPayloads,
          [event.id]: data,
        };
        return;
      }
      if (!options.silent) {
        this.feedback = this.extractError(
          data,
          "No pudimos cargar categorías oficiales."
        );
        this.feedbackType = "error";
      }
    },
    async createOfficialTicket(event) {
      if (!this.canCreateOfficialTicket(event) || this.savingOfficialTicketKey)
        return;
      const form = this.officialTicketForm(event);
      this.savingOfficialTicketKey = `tickets-${event.id}`;
      this.feedback = "";
      const data = await apiService(
        `/api/admin/swap/events/${event.id}/official-tickets/`,
        "POST",
        {
          name: String(form.name || "").trim(),
          description: String(form.description || "").trim(),
          price: form.price,
          quantity: Number(form.quantity || 1),
        }
      );
      this.savingOfficialTicketKey = "";
      if (data && !data.err && Array.isArray(data.options)) {
        this.officialTicketPayloads = {
          ...this.officialTicketPayloads,
          [event.id]: data,
        };
        this.officialTicketForms = {
          ...this.officialTicketForms,
          [event.id]: this.defaultOfficialTicketForm(),
        };
        this.feedback = `Categoría oficial agregada para ${event.name}`;
        this.feedbackType = "success";
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos agregar la categoría oficial."
      );
      this.feedbackType = "error";
    },
    async reviewSeller(seller, action) {
      if (!seller || !seller.id || this.reviewingKey) return;
      this.reviewingKey = `seller-${seller.id}`;
      const note =
        action === "approve"
          ? "Aprobado desde admin Swap."
          : action === "block"
          ? "Bloqueado desde admin Swap."
          : "Marcado para revisión desde admin Swap.";
      const data = await apiService(
        `/api/admin/swap/sellers/${seller.id}/review/`,
        "POST",
        { action, note }
      );
      this.reviewingKey = "";
      if (data && !data.err && data.id) {
        this.feedback = `Vendedor actualizado: ${
          data.public_name || data.user_email || data.id
        }`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos actualizar el vendedor."
      );
      this.feedbackType = "error";
    },
    async reviewListing(listing, action) {
      if (!listing || !listing.id || this.reviewingKey) return;
      this.reviewingKey = `listing-${listing.id}`;
      const note =
        action === "approve"
          ? "Listing aprobado desde admin Swap."
          : action === "reject"
          ? "Listing rechazado desde admin Swap."
          : "Listing marcado para revisión desde admin Swap.";
      const data = await apiService(
        `/api/admin/swap/listings/${listing.id}/review/`,
        "POST",
        { action, note }
      );
      this.reviewingKey = "";
      if (data && !data.err && data.id) {
        this.feedback = `Entrada actualizada: ${
          data.title || data.event_name || data.id
        }`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos actualizar la entrada."
      );
      this.feedbackType = "error";
    },
    async verifyOrderPayment(order, status) {
      if (!order || !order.order_number || this.verifyingKey) return;
      this.verifyingKey = `order-${order.order_number}`;
      const payload = {
        status,
        provider_reference: this.verificationField(order, "provider_reference"),
        note: this.verificationField(order, "note"),
      };
      const data = await apiService(
        `/api/admin/swap/orders/${encodeURIComponent(
          order.order_number
        )}/payment-verification/`,
        "POST",
        payload
      );
      this.verifyingKey = "";
      if (data && !data.err && data.order_number) {
        this.feedback = `Compra actualizada: ${data.order_number}`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos actualizar la verificación de pago."
      );
      this.feedbackType = "error";
    },
    async submitManualRefund(order) {
      if (!this.canManualRefund(order)) return;
      this.manualRefundingKey = `refund-${order.order_number}`;
      this.feedback = "";
      const form = this.manualRefundForms[order.order_number] || {};
      const payload = new FormData();
      payload.append("amount", form.amount || "");
      payload.append("provider", form.provider || "paypal");
      payload.append("provider_reference", form.provider_reference || "");
      payload.append("note", form.note || "");
      payload.append("reason", "manual_refund");
      this.manualRefundAllocationIds(order).forEach((allocationId) =>
        payload.append("allocation_ids", String(allocationId))
      );
      if (form.proof_file) payload.append("proof_file", form.proof_file);
      const data = await apiServiceForm(
        `/api/admin/swap/orders/${encodeURIComponent(
          order.order_number
        )}/manual-refund/`,
        "POST",
        payload,
        { timeoutMs: 60000 }
      );
      this.manualRefundingKey = "";
      if (data && !data.err && data.order_number) {
        this.feedback = `Reembolso manual registrado para ${data.order_number}`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos registrar el reembolso manual."
      );
      this.feedbackType = "error";
    },
    async updateDeliveryStatus(order, delivery, status) {
      if (!this.canUpdateDelivery(order, delivery, status)) return;
      this.updatingDeliveryKey = `delivery-${delivery.id}-${status}`;
      const note =
        status === "buyer_accessible"
          ? "Delivery habilitado al buyer desde admin Swap."
          : "Delivery movido a progreso desde admin Swap.";
      let data;
      if (status === "buyer_accessible") {
        const form = this.deliveryArtifactForms[delivery.id] || {};
        const payload = new FormData();
        payload.append("status", status);
        payload.append("note", note);
        payload.append("release_to_buyer", "true");
        payload.append("artifact_title", form.artifact_title || "");
        payload.append("external_reference", form.external_reference || "");
        payload.append("external_url", form.external_url || "");
        payload.append("instructions", form.instructions || "");
        if (form.artifact_file)
          payload.append("artifact_file", form.artifact_file);
        if (form.artifact_file) payload.append("artifact_type", "file");
        else if (form.external_url)
          payload.append("artifact_type", "external_url");
        else if (form.external_reference)
          payload.append("artifact_type", "external_reference");
        else if (form.instructions)
          payload.append("artifact_type", "instructions");
        data = await apiServiceForm(
          `/api/admin/swap/deliveries/${delivery.id}/status/`,
          "POST",
          payload,
          { timeoutMs: 60000 }
        );
      } else {
        data = await apiService(
          `/api/admin/swap/deliveries/${delivery.id}/status/`,
          "POST",
          { status, note }
        );
      }
      this.updatingDeliveryKey = "";
      if (data && !data.err && data.order_number) {
        this.feedback = `Entrega actualizada en la compra ${data.order_number}`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos actualizar la entrega."
      );
      this.feedbackType = "error";
    },
    messageDraft(threadId) {
      return this.messageDrafts[threadId] || "";
    },
    setMessageDraft(threadId, value) {
      this.messageDrafts = { ...this.messageDrafts, [threadId]: value };
    },
    async sendAdminMessage(thread) {
      if (!thread || !thread.id || this.sendingMessageKey) return;
      const body = String(this.messageDraft(thread.id) || "").trim();
      if (!body) return;
      this.sendingMessageKey = `thread-${thread.id}`;
      const data = await apiService(
        `/api/admin/swap/messages/${thread.id}/`,
        "POST",
        { body }
      );
      this.sendingMessageKey = "";
      if (data && data.id && !data.err) {
        this.setMessageDraft(thread.id, "");
        this.feedback = "Mensaje FOMO enviado.";
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos enviar el mensaje Swap."
      );
      this.feedbackType = "error";
    },
    async reviewDispute(dispute, action) {
      if (!dispute || !dispute.id || this.reviewingDisputeKey) return;
      this.reviewingDisputeKey = `dispute-${dispute.id}`;
      const payload = {
        action,
        resolution: this.disputeReviewField(dispute, "resolution"),
      };
      const data = await apiService(
        `/api/admin/swap/disputes/${dispute.id}/review/`,
        "POST",
        payload
      );
      this.reviewingDisputeKey = "";
      if (data && !data.err && data.id) {
        this.feedback = `Reclamo actualizado: ${data.order_number || data.id}`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos actualizar el reclamo swap."
      );
      this.feedbackType = "error";
    },
    async releaseEligiblePayouts() {
      if (this.releasingPayouts) return;
      this.releasingPayouts = true;
      this.feedback = "";
      const data = await apiService(
        "/api/admin/swap/payouts/release-eligible/",
        "POST",
        {}
      );
      this.releasingPayouts = false;
      if (
        data &&
        !data.err &&
        Object.prototype.hasOwnProperty.call(data, "released")
      ) {
        this.feedback = `Evaluación completada: ${data.released} saldos liberados, ${data.orders_releasable} compras disponibles y ${data.skipped_disputed} saldos retenidos por reclamos.`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(data, "No pudimos evaluar los saldos.");
      this.feedbackType = "error";
    },
    async reservePayoutOrders() {
      if (this.reservingPayouts) return;
      this.reservingPayouts = true;
      this.feedback = "";
      const data = await apiService(
        "/api/admin/swap/payouts/reserve/",
        "POST",
        {}
      );
      this.reservingPayouts = false;
      if (
        data &&
        !data.err &&
        Object.prototype.hasOwnProperty.call(data, "payout_orders")
      ) {
        this.feedback = `Preparación completada: ${data.payout_orders} órdenes, ${data.reserved_allocations} saldos reservados y ${data.skipped_blocked} bloqueados por el destinatario.`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos preparar las órdenes."
      );
      this.feedbackType = "error";
    },
    async syncPayoutOrders() {
      if (this.syncingPayouts) return;
      this.syncingPayouts = true;
      this.feedback = "";
      const data = await apiService(
        "/api/admin/swap/payouts/sync-orders/",
        "POST",
        {}
      );
      this.syncingPayouts = false;
      if (
        data &&
        !data.err &&
        Object.prototype.hasOwnProperty.call(data, "paid_allocations")
      ) {
        this.feedback = `Estados actualizados: ${data.paid_allocations} saldos pagados y ${data.released_allocations} reservas liberadas.`;
        this.feedbackType = "success";
        await this.loadData({ preserveFeedback: true });
        return;
      }
      this.feedback = this.extractError(
        data,
        "No pudimos sincronizar los pagos."
      );
      this.feedbackType = "error";
    },
    openEvent(event) {
      if (!event || !event.id) return;
      this.$router.push({
        name: "EventHostView2",
        params: { slug: event.id },
        query: { section: "editar" },
      });
    },
    openCheckout(event) {
      if (!event || !event.slug) return;
      window.open(`/event/${event.slug}/?preview=1`, "_blank", "noopener");
    },
  },
};
</script>

<style scoped>
.admin-swap-shell {
  --text-main: var(--fomo-dashboard-text);
  --text-soft: var(--fomo-dashboard-copy);
  display: grid;
  gap: 28px;
  min-width: 0;
  color: var(--fomo-dashboard-text);
  font-family: var(--fomo-font-body);
  text-align: left;
  color-scheme: dark;
}
.admin-swap-shell :is(h1, h2, h3, h4, p) {
  margin: 0;
}
.admin-swap-shell :is(h1, h2, h3) {
  color: var(--fomo-dashboard-text);
  font-family: var(--fomo-font-heading) !important;
  font-weight: 700;
  letter-spacing: -0.045em;
  text-align: left;
}
.admin-swap-shell h1 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: clamp(32px, 3.2vw, 44px);
  line-height: 1.12;
  margin: 8px 0 12px;
}
.admin-swap-shell h2 {
  font-size: 27px;
  line-height: 1.2;
}
.admin-swap-shell h3 {
  font-size: 23px;
  line-height: 1.25;
}
.admin-swap-shell p {
  color: var(--fomo-dashboard-copy);
  font: 14px/1.65 var(--fomo-font-body);
}
.admin-swap-shell strong,
.admin-swap-shell b {
  color: var(--fomo-dashboard-text);
  font-family: var(--fomo-font-body);
}
.swap-heading,
.swap-section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}
.swap-heading {
  padding: 12px 0 0;
}
.swap-heading-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.admin-swap-shell .fomo-button.event-view__action {
  width: auto;
  min-width: 0;
  max-width: 100%;
}
.swap-eyebrow,
.swap-step {
  color: var(--fomo-dashboard-label);
  font: 500 11px/1.5 var(--fomo-font-label);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.swap-market {
  color: var(--fomo-dashboard-label);
  border: 1px solid var(--fomo-border);
  border-radius: 8px;
  padding: 6px 9px;
  font: 500 11px/1.4 var(--fomo-font-label);
  letter-spacing: 0.04em;
}
.swap-nav {
  display: flex;
  align-items: stretch;
  gap: 4px;
  border-bottom: 1px solid var(--fomo-border);
  padding-bottom: 0;
  overflow-x: auto;
}
.swap-nav button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1 0 auto;
  min-height: 52px;
  padding: 12px 14px;
  background: transparent;
  color: var(--fomo-dashboard-copy);
  border: 0;
  border-bottom: 2px solid transparent;
  font: 600 13px/1.4 var(--fomo-font-button);
  cursor: pointer;
}
.swap-nav button.is-active {
  color: var(--fomo-dashboard-text);
  border-bottom-color: var(--fomo-dashboard-label);
}
.swap-nav button:hover {
  color: var(--fomo-dashboard-text);
  background: rgba(196, 206, 228, 0.04);
}
.swap-nav b,
.swap-review-tabs b {
  border: 1px solid var(--fomo-border);
  border-radius: 6px;
  font: 500 10px/1.5 var(--fomo-font-label);
  padding: 1px 5px;
}
.admin-swap-shell
  :is(button, summary, input, select, textarea, a):focus-visible {
  outline: 2px solid var(--fomo-dashboard-label);
  outline-offset: 4px;
}
.swap-section {
  display: grid;
  gap: 24px;
  min-width: 0;
}
.swap-section:focus {
  outline: none;
}
.swap-section-heading {
  min-width: 0;
}
.swap-section-heading h3 {
  margin-top: 6px;
}
.swap-section-heading p {
  margin-top: 8px;
}
.swap-caption {
  color: var(--fomo-dashboard-copy);
  font: 12px/1.5 var(--fomo-font-body);
}
.swap-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.swap-metric {
  display: grid;
  gap: 12px;
  padding: 24px;
  text-align: left;
  color: var(--fomo-dashboard-label);
  cursor: pointer;
}
.swap-metric-top,
.swap-metric-bottom {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}
.swap-metric-top {
  font: 500 12px/1.5 var(--fomo-font-body);
}
.swap-metric-top > i {
  font-size: 17px;
}
.swap-metric > strong {
  font: 600 40px/1.1 var(--fomo-font-body);
  letter-spacing: -0.05em;
}
.swap-metric-bottom {
  font: 11px/1.5 var(--fomo-font-body);
  color: var(--fomo-dashboard-copy);
}
.swap-metric-bottom > i {
  font-size: 10px;
}
.swap-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}
.swap-panel {
  padding: 28px;
  min-width: 0;
}
.swap-panel-icon {
  color: var(--fomo-dashboard-copy);
  font-size: 20px;
}
.swap-task-list {
  margin-top: 20px;
}
.swap-task,
.swap-event-preview {
  display: grid;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 18px 0;
  border: 0;
  border-top: 1px solid var(--fomo-border);
  background: transparent;
  color: var(--fomo-dashboard-label);
  text-align: left;
  cursor: pointer;
}
.swap-task {
  grid-template-columns: 38px minmax(0, 1fr) auto 10px;
}
.swap-task > span:nth-child(2),
.swap-event-preview > span:nth-child(2) {
  display: grid;
  gap: 4px;
  min-width: 0;
}
.swap-task strong,
.swap-event-preview strong {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
}
.swap-task small,
.swap-event-preview small {
  font: 12px/1.5 var(--fomo-font-body);
  color: var(--fomo-dashboard-copy);
}
.swap-task > b {
  font-size: 15px;
  font-weight: 500;
}
.swap-task > i,
.swap-event-preview > i {
  font-size: 10px;
}
.swap-task-icon,
.swap-record-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--fomo-border);
  border-radius: 12px;
  color: var(--fomo-dashboard-label);
  background: var(--fomo-control);
  font-size: 14px;
}
.swap-event-preview {
  grid-template-columns: 46px minmax(0, 1fr) 10px;
}
.swap-panel > .swap-event-preview:first-of-type {
  margin-top: 20px;
}
.swap-date {
  display: grid;
  align-content: center;
  gap: 0;
  width: 46px;
  min-height: 52px;
  border: 1px solid var(--fomo-border);
  border-radius: 12px;
  text-align: center;
  background: var(--fomo-control);
}
.swap-date b {
  font: 500 20px/1.2 var(--fomo-font-body);
}
.swap-date small {
  color: var(--fomo-dashboard-copy);
  font: 10px/1.6 var(--fomo-font-label);
  text-transform: uppercase;
}
.swap-policy {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  color: var(--fomo-dashboard-copy);
  font: 12px/1.6 var(--fomo-font-body);
}
.swap-policy > span {
  flex: 1;
}
.swap-policy .fomo-button {
  flex-shrink: 0;
}
.swap-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}
.swap-search {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 280px;
  min-width: 0;
  background: var(--fomo-control);
  border: 1px solid var(--fomo-border);
  border-radius: 12px;
  padding: 0 16px;
  color: var(--fomo-dashboard-copy);
}
.swap-search input {
  width: 100%;
  min-width: 0;
  min-height: 46px;
  background: transparent;
  border: 0;
  outline-offset: 0;
  color: var(--fomo-dashboard-text);
  font: 13px/1.5 var(--fomo-font-body);
}
.swap-search input::placeholder {
  color: var(--fomo-dashboard-copy);
  opacity: 0.85;
}
/* Global legacy input rules use !important; keep this workspace on shared controls. */
.admin-swap-shell .swap-search input {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
}
.admin-swap-shell .swap-search:focus-within {
  outline: 2px solid var(--fomo-dashboard-label);
  outline-offset: 3px;
}
.admin-swap-shell .swap-search input:focus {
  box-shadow: none !important;
  outline: none;
}
.admin-swap-shell .swap-select select,
.admin-swap-shell .workspace-input {
  background: var(--fomo-control) !important;
  border: 1px solid var(--fomo-border) !important;
  border-radius: 12px !important;
}
.admin-swap-shell .workspace-input:focus {
  box-shadow: none !important;
}
.admin-swap-shell .fomo-card {
  border-radius: 16px;
}
.swap-select {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--fomo-dashboard-copy);
  font: 12px/1.5 var(--fomo-font-body);
}
.swap-select select {
  min-height: 46px;
  max-width: 100%;
  border: 1px solid var(--fomo-border);
  border-radius: 12px;
  background: var(--fomo-control);
  color: var(--fomo-dashboard-label);
  padding: 10px 32px 10px 12px;
  font: 13px/1.5 var(--fomo-font-body);
}
.swap-review-tabs {
  display: flex;
  gap: 8px;
}
.swap-review-tabs button {
  display: flex;
  gap: 10px;
  align-items: center;
  min-height: 44px;
  border: 1px solid var(--fomo-border);
  border-radius: 10px;
  background: transparent;
  color: var(--fomo-dashboard-copy);
  padding: 10px 16px;
  font: 600 13px/1.5 var(--fomo-font-button);
  cursor: pointer;
}
.swap-review-tabs button.is-active {
  color: var(--fomo-dashboard-text);
  background: var(--fomo-control);
  border-color: var(--fomo-dashboard-copy);
}
.swap-record-list {
  display: grid;
  gap: 12px;
  min-width: 0;
}
.swap-record {
  min-width: 0;
}
.swap-record-summary {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 20px;
  padding: 22px 24px;
  cursor: pointer;
  list-style: none;
}
.swap-record-summary::-webkit-details-marker {
  display: none;
}
.swap-record-title,
.swap-record-meta {
  display: grid;
  gap: 6px;
  min-width: 0;
}
.swap-record-title strong {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.swap-record-title small,
.swap-record-meta small {
  color: var(--fomo-dashboard-copy);
  font: 12px/1.5 var(--fomo-font-body);
}
.swap-record-meta {
  justify-items: end;
  text-align: right;
  font: 12px/1.5 var(--fomo-font-body);
}
.swap-record-meta > strong {
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
}
.swap-open-label {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  color: var(--fomo-dashboard-label);
  min-width: 84px;
  font: 12px/1.5 var(--fomo-font-button);
}
.swap-open-label > i {
  font-size: 10px;
  transition: transform 0.15s;
}
.swap-record[open] > summary .swap-open-label > i {
  transform: rotate(180deg);
}
.swap-record-body {
  display: grid;
  gap: 20px;
  margin: 0 24px;
  padding: 24px 0;
  border-top: 1px solid var(--fomo-border);
  min-width: 0;
}
.swap-inline-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  color: var(--fomo-dashboard-copy);
  font-size: 12px;
}
.admin-swap-shell .workspace-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 10px;
  margin: 0;
}
.admin-swap-shell .workspace-status-pill {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  gap: 6px;
  padding: 5px 9px;
  border: 1px solid var(--fomo-border);
  border-radius: 7px;
  background: rgba(196, 206, 228, 0.055);
  color: var(--fomo-dashboard-label);
  box-shadow: none;
  font: 500 11px/1.4 var(--fomo-font-label);
  text-transform: none;
  letter-spacing: 0;
  text-align: left;
}
.admin-swap-shell .workspace-status-pill::before {
  content: "";
  width: 5px;
  height: 5px;
  flex-shrink: 0;
  border-radius: 50%;
  background: currentColor;
}
.admin-swap-shell .workspace-status-pill.is-warning {
  color: #e4cfaa;
}
.admin-swap-shell .workspace-status-pill.is-danger {
  color: #f0b6bd;
}
.admin-swap-shell .workspace-status-pill.is-active {
  color: var(--fomo-dashboard-label);
}
.swap-empty {
  display: grid;
  justify-items: center;
  gap: 12px;
  padding: 48px 24px;
  color: var(--fomo-dashboard-copy);
  font: 14px/1.6 var(--fomo-font-body);
  text-align: center;
}
.swap-empty > i {
  font-size: 24px;
  margin-bottom: 4px;
  color: var(--fomo-dashboard-label);
}
.swap-empty > strong {
  font-size: 16px;
  font-weight: 600;
}
.swap-empty p {
  max-width: 360px;
}
.swap-feedback {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid var(--fomo-border);
  border-radius: 12px;
  background: var(--fomo-control);
  color: var(--fomo-dashboard-label);
  font-size: 13px;
  line-height: 1.5;
}
.swap-feedback > span {
  flex: 1;
}
.swap-feedback.is-error {
  border-color: rgba(240, 182, 189, 0.4);
  color: #f0b6bd;
}
.admin-swap-shell .workspace-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-top: 28px;
}
.admin-swap-shell .workspace-field {
  display: grid;
  align-content: start;
  min-width: 0;
  gap: 8px;
  margin: 0;
}
.admin-swap-shell .workspace-field--wide {
  grid-column: 1/-1;
}
.admin-swap-shell .workspace-field > span {
  color: var(--fomo-dashboard-label);
  font: 500 12px/1.5 var(--fomo-font-body);
  text-transform: none;
  letter-spacing: 0;
}
.admin-swap-shell .workspace-input {
  width: 100%;
  min-width: 0;
  min-height: 46px;
  padding: 12px 14px;
  border: 1px solid var(--fomo-border);
  border-radius: 12px;
  background: var(--fomo-control);
  color: var(--fomo-dashboard-text);
  box-shadow: none;
  font: 13px/1.5 var(--fomo-font-body);
}
.admin-swap-shell .workspace-input::placeholder {
  color: var(--fomo-dashboard-copy);
  opacity: 0.8;
}
.admin-swap-shell textarea {
  resize: vertical;
}
.swap-field-help {
  margin-top: 20px !important;
  font-size: 12px !important;
}
.swap-form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  border-top: 1px solid var(--fomo-border);
  margin-top: 24px;
  padding-top: 24px;
}
.admin-swap-create {
  max-width: 920px;
  width: 100%;
  justify-self: center;
}
.admin-swap-official-categories {
  display: grid;
  gap: 16px;
}
.admin-swap-official-head,
.admin-swap-refund-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}
.admin-swap-official-head > div,
.admin-swap-refund-heading > div {
  display: grid;
  gap: 4px;
}
.admin-swap-official-head small,
.admin-swap-refund-heading small {
  color: var(--fomo-dashboard-label);
  font: 12px/1.5 var(--fomo-font-label);
}
.admin-swap-official-head strong,
.admin-swap-refund-heading strong {
  font-size: 14px;
  font-weight: 600;
}
.admin-swap-official-list {
  display: grid;
}
.admin-swap-official-pill {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--fomo-border);
}
.admin-swap-official-pill span {
  color: var(--fomo-dashboard-copy);
  font-size: 11px;
}
.admin-swap-official-pill strong {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
}
.admin-swap-official-pill em {
  font: 12px/1.5 var(--fomo-font-body);
  color: var(--fomo-dashboard-label);
}
.swap-subdetails > summary,
.admin-swap-refund-details > summary {
  padding: 14px 0;
  cursor: pointer;
  font: 600 13px/1.5 var(--fomo-font-button);
  color: var(--fomo-dashboard-label);
}
.admin-swap-official-form,
.admin-swap-payment-form,
.admin-swap-manual-refund-form,
.admin-swap-delivery-artifact-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: end;
  padding-top: 8px;
}
.admin-swap-official-form > .fomo-button,
.admin-swap-manual-refund-form > .fomo-button {
  justify-self: start;
}
.admin-swap-official-description,
.admin-swap-artifact-note,
.admin-swap-refund-heading {
  grid-column: 1/-1;
}
.admin-swap-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  font: 12px/1.6 var(--fomo-font-body);
  color: var(--fomo-dashboard-copy);
}
.admin-swap-card-meta > span {
  display: flex;
  align-items: center;
  gap: 8px;
}
.admin-swap-proof-strip {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 20px;
}
.admin-swap-proof-strip > div {
  display: grid;
  gap: 6px;
}
.admin-swap-proof-strip strong {
  font-size: 14px;
  font-weight: 600;
}
.admin-swap-proof-strip :is(small, span) {
  color: var(--fomo-dashboard-copy);
  font-size: 12px;
}
.admin-swap-shell a {
  color: var(--fomo-dashboard-label);
  text-decoration: underline;
  text-underline-offset: 3px;
  font: 600 12px/1.5 var(--fomo-font-body);
  overflow-wrap: anywhere;
}
.admin-swap-order-meta,
.admin-swap-finance-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  color: var(--fomo-dashboard-copy);
  font-size: 12px;
}
.admin-swap-order-meta span {
  display: flex;
  align-items: center;
  gap: 8px;
}
.swap-subheading {
  font: 600 15px/1.4 var(--fomo-font-body);
  color: var(--fomo-dashboard-text);
}
.admin-swap-delivery-review {
  display: grid;
  gap: 16px;
}
.admin-swap-delivery-row {
  display: grid;
  gap: 16px;
  padding: 20px;
}
.admin-swap-delivery-row > div:first-child {
  display: grid;
  gap: 8px;
}
.admin-swap-delivery-row strong {
  font-size: 14px;
}
.admin-swap-delivery-row span,
.admin-swap-refund-heading > span {
  font-size: 12px;
  color: var(--fomo-dashboard-copy);
}
.admin-swap-artifact-list {
  display: grid;
  gap: 12px;
}
.admin-swap-artifact-list article {
  display: grid;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--fomo-border);
}
.admin-swap-artifact-list small {
  font: 11px/1.5 var(--fomo-font-label);
  color: var(--fomo-dashboard-label);
}
.admin-swap-refund-details {
  padding: 8px 20px 20px;
}
.admin-swap-thread-messages {
  display: grid;
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
}
.admin-swap-thread-message {
  display: grid;
  gap: 6px;
  padding: 16px;
  background: var(--fomo-control);
  border: 1px solid var(--fomo-border);
  border-radius: 14px;
  max-width: 90%;
  justify-self: start;
}
.admin-swap-thread-message.is-admin {
  justify-self: end;
}
.admin-swap-thread-message small {
  color: var(--fomo-dashboard-label);
  font: 500 11px/1.5 var(--fomo-font-label);
}
.admin-swap-thread-message span {
  color: var(--fomo-dashboard-text);
  overflow-wrap: anywhere;
  font: 13px/1.6 var(--fomo-font-body);
}
.admin-swap-message-compose {
  display: grid;
  gap: 12px;
}
.admin-swap-message-compose > .fomo-button {
  justify-self: end;
}
.swap-payout-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}
.swap-payout-grid .swap-panel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
}
.swap-payout-grid p {
  flex: 1;
}
.swap-payout-grid .fomo-button {
  margin-top: 12px;
}
.swap-payout-icon {
  font-size: 26px;
  color: var(--fomo-dashboard-label);
  margin-top: 8px;
}
.admin-swap-orders-panel {
  display: grid;
  gap: 24px;
  min-width: 0;
}
@media (max-width: 1100px) {
  .swap-metrics {
    gap: 12px;
  }
  .swap-metric {
    padding: 20px;
  }
  .swap-overview-grid {
    grid-template-columns: 1fr;
  }
  .swap-heading {
    align-items: flex-start;
  }
  .swap-heading-actions {
    flex-direction: column-reverse;
    align-items: flex-end;
  }
  .swap-panel {
    padding: 24px;
  }
  .swap-payout-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 700px) {
  .admin-swap-shell {
    gap: 24px;
  }
  .swap-heading {
    flex-direction: column;
    gap: 20px;
  }
  .swap-heading-actions {
    flex-direction: row;
    align-items: center;
    width: 100%;
    justify-content: space-between;
  }
  .admin-swap-shell h1 {
    font-size: 34px;
  }
  .admin-swap-shell h2 {
    font-size: 23px;
  }
  .admin-swap-shell h3 {
    font-size: 21px;
  }
  .swap-nav {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
    overflow: visible;
  }
  .swap-nav button {
    flex-direction: column;
    gap: 6px;
    font-size: 11px;
    padding: 12px 4px;
    position: relative;
  }
  .swap-nav button > b {
    position: absolute;
    right: 4px;
    top: 4px;
  }
  .swap-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .swap-metric {
    padding: 20px 16px;
    gap: 12px;
  }
  .swap-metric-top {
    align-items: start;
    font-size: 11px;
    gap: 8px;
  }
  .swap-metric-top > i {
    font-size: 13px;
  }
  .swap-metric > strong {
    font-size: 32px;
  }
  .swap-metric-bottom {
    font-size: 10px;
  }
  .swap-metric-bottom > i {
    display: none;
  }
  .swap-section-heading {
    flex-wrap: wrap;
    gap: 12px;
  }
  .swap-section-heading > .swap-caption {
    width: 100%;
  }
  .swap-panel {
    padding: 24px 20px;
  }
  .swap-task {
    grid-template-columns: 32px minmax(0, 1fr) auto 8px;
    gap: 10px;
  }
  .swap-task-icon {
    width: 32px;
    height: 36px;
  }
  .swap-task small {
    font-size: 11px;
  }
  .swap-policy {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .swap-policy > span {
    flex-basis: calc(100% - 36px);
  }
  .swap-policy > i {
    padding-top: 3px;
  }
  .swap-policy .fomo-button {
    margin-left: 20px;
  }
  .swap-toolbar {
    gap: 12px;
  }
  .swap-toolbar .swap-search {
    flex-basis: 100%;
  }
  .swap-select {
    flex: 1;
    min-width: 0;
  }
  .swap-select select {
    min-width: 0;
    flex: 1;
  }
  .swap-record-summary {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 14px 12px;
    padding: 20px;
  }
  .swap-record-summary > .swap-record-meta,
  .swap-record-summary > .workspace-status-pill,
  .swap-record-summary > .swap-caption {
    grid-column: 2;
    justify-items: start;
    text-align: left;
  }
  .swap-record-summary > .swap-open-label {
    grid-column: 2;
    justify-content: flex-start;
  }
  .swap-record-title strong {
    font-size: 14px;
  }
  .swap-record-body {
    margin: 0 20px;
    padding: 20px 0;
  }
  .admin-swap-shell .workspace-form,
  .admin-swap-official-form,
  .admin-swap-payment-form,
  .admin-swap-manual-refund-form,
  .admin-swap-delivery-artifact-form {
    grid-template-columns: 1fr;
  }
  .admin-swap-official-head,
  .admin-swap-refund-heading,
  .admin-swap-proof-strip {
    flex-wrap: wrap;
  }
  .admin-swap-official-pill {
    align-items: flex-start;
  }
  .admin-swap-official-pill em {
    flex-basis: 100%;
  }
  .admin-swap-thread-message {
    max-width: 100%;
  }
  .swap-form-actions {
    flex-wrap: wrap;
  }
  .admin-swap-shell .workspace-actions > .fomo-button {
    max-width: 100%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .swap-open-label > i {
    transition: none;
  }
}
</style>
